import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, API, idr } from "../lib/api";
import { hargaSaatPilihUnit, ringkasInfoUnit, terimaPilihanJual } from "../lib/hargaUnit";
import {
  Plus, Trash2, X, Download, Phone, MessageCircle, Send, CreditCard,
  Repeat, Split, Pencil, Clock, Lock, History, FileText, Info, Upload,
  Sparkles, Copy, ExternalLink, Printer, Undo2, AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { bolehPakaiAI } from "../lib/izinAI";
import { hitungNominalReturDefault, susunReturDariNota, syaratTukarUnit, syaratTagihanRetur } from "../lib/retur";
import GerbangLengkapiNoHp from "../components/retur/GerbangLengkapiNoHp";
import FakturPrint, { isStruk } from "../components/faktur/FakturPrint";
import StrukPrint from "../components/faktur/StrukPrint";
import PemilihTemplate from "../components/faktur/PemilihTemplate";
import InputRupiah from "../components/InputRupiah";
import { useCabangOptions } from "../lib/cabang";
import { DatePicker, MonthPicker } from "@/components/ui/date-picker";
import LoadingState from "@/components/ui/loading-state";
import { hariIniISO, bulanIniISO, formatTanggal } from "@/lib/tanggal";
import {
  TabelDesktop, DaftarKartuMobile, KartuBaris, KartuBarisHeader, KartuBarisMeta,
} from "@/components/ui/responsive-table";
import SourceInfoField, { useSourceInfoConfig, validasiSourceInfo } from "../components/SourceInfoField";
import PilihPelangganNota from "../components/pelanggan/PilihPelangganNota";
import useAturanIdentitasNota from "../hooks/useAturanIdentitasNota";
import {
  periksaBentukHp, kunciNama, butuhKartu, hpBerubah as hpBerubahLib, hpTampilan,
} from "../lib/identitasNota";
import {
  PaymentMethodSelect, MetodePembayaranManager, TombolKelolaMetode, bolehKelolaMetode,
  adalahKreditRetur,
} from "../components/pembayaran/MetodePembayaran";
import PetunjukKreditRetur from "../components/pembayaran/PetunjukKreditRetur";
import useKreditRetur from "../hooks/useKreditRetur";
import { rincianFormPenjualan } from "../lib/rincianBayar";
import {
  diskonBaris, diskonBarisTotal, ambilDiskonLama, diskonEfektifForm, adminEfektifForm,
  galatCampurDiskon, galatDiskonBaris, itemsFormDariDokumen, itemsKePayload, bagianDiskonPayload,
  barisBaru, barisPembayaranDariDokumen, ikutkanSisa, aturBarisUtamaTempo, galatBarisPembayaran,
  bagianBayarPayload, adalahKreditReturMetode, ringkasBlokPembayaran, rekeningPengembalian,
  rekeningLengkap, galatRekeningPengembalian, bagianRekeningPayload,
} from "../lib/payloadNota";
import BlokPembayaran from "../components/pembayaran/BlokPembayaran";
import DaftarBarisPembayaran from "../components/pembayaran/DaftarBarisPembayaran";
import { useModalGuard, useModalLayer, UnsavedGuardBar } from "../hooks/useModalGuard";
import { useSortState, sortRows, SortTh } from "@/components/ui/sort";
import {
  KATEGORI_ITEM, KATEGORI_DEFAULT, labelKategori, normalKategori, deriveKode, deriveKategoriTeksBebas,
} from "@/lib/kategoriBarang";
import BundleKomponenPicker, {
  useResepBundlingAktif, useProdukInfoCache, bangunKomponenKosong,
  cariAlasanBundleBelumSiap, kumpulkanSemuaImeiForm,
} from "@/components/bundling/BundleKomponenPicker";

const nowTime = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const emptyForm = () => ({
  id: null,
  tanggal: hariIniISO(),
  waktu: nowTime(),
  nama_pelanggan: "", no_hp: "",
  items: [{ nama_barang: "", harga: 0, kode: "HP", kategori: KATEGORI_DEFAULT, sku: "", imei: "", is_bonus: false, komponen: null, diskon_rp: 0 }],
  imei: "", metode_pembayaran: "Cash", trade_in: false, trade_in_note: "",
  trade_in_items: [],
  harga_terima: 0, catatan: "", nama_penjual: "", branch: "",
  // Diskon PER BARANG (brief owner 22 Sep 2026, D2+D3) di `items[].diskon_rp`
  // + satu PIC per nota. Kolom diskon nota & biaya admin tidak ada lagi di form;
  // `_diskon_lama` = potret nilai LAMA transaksi yang dibuka (baca-saja).
  diskon_pic_approval: "", _diskon_lama: null,
  // PPN (keputusan owner 18 Agu 2026) — toggle tingkat DOKUMEN. Tarif &
  // nominal sesungguhnya DIHITUNG SERVER (routers/sales.py) — angka yang
  // dipratinjaukan di sini murni untuk kasir, bukan sumber kebenaran.
  kena_ppn: false,
  is_split: false,
  // Transaksi BARU memakai blok pembayaran berbaris (batch N3, brief 22 Sep
  // D4) — `skema_nota` 3, server menandai dokumennya `bayar_per_baris`.
  // Transaksi LAMA yang dibuka untuk diedit tetap memakai form lama (lihat
  // openEdit): layar ini tidak mengubah bentuk pembayaran dokumen lama.
  _bayar_per_baris: true,
  payments: [barisBaru("Cash", 0, true)],
  // Rekening pengembalian nota DOWNGRADE (tinjauan 1 N3) — server mewajibkannya
  // untuk transaksi bertanda; `_rekening_terkunci` = rekening dokumen yang
  // dibuka sudah lengkap (server menolak perubahan lewat edit nota).
  refund_nama_bank: "", refund_no_rekening: "", refund_atas_nama: "", _rekening_terkunci: false,
  // Sumber Info / atribusi marketing (brief bos 11 Agu 2026, WAJIB selama
  // saklar darurat aktif — lihat components/SourceInfoField.jsx). "" (bukan
  // null) supaya <select> terkontrol dari awal render.
  source_info: "", source_ref_id: "",
});

// Pratinjau SAJA — lihat komentar TARIF_PPN_DEFAULT di backend
// (routers/finance/_shared.py): tarif yang benar-benar tersimpan datang dari
// respons server, tidak pernah dari konstanta ini.
const PPN_PERSEN_PREVIEW = 11;

// Label status TAMPILAN piutang (routers/finance/_shared.py
// `_status_tampilan_ap_ar`, dikirim get_sale sebagai `_piutang_nota.status`).
// `belum_lunas` = status KASAR untuk pembaca tanpa izin finance_piutang
// (routers/sales.py `ringkas_piutang_nota(lengkap=False)`).
// Kunci yang tidak dikenal ditampilkan mentah — kelihatan salah, bukan kosong.
const STATUS_PIUTANG_LABEL = {
  belum_diverifikasi: "Menunggu verifikasi Finance",
  perlu_perbaikan: "Perlu perbaikan",
  aktif: "Belum dibayar",
  terbayar_parsial: "Terbayar sebagian",
  belum_lunas: "Belum lunas",
  lunas: "Lunas",
  dibatalkan: "Dibatalkan",
};

// Format 'YYYY-MM-DD HH:MM' → 'DD Mmm HH:MM'
const fmtDateTime = (d, t) => {
  if (!d) return "-";
  const dt = new Date(d);
  const m = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];
  const s = `${dt.getDate()} ${m[dt.getMonth()]}`;
  return t ? `${s} · ${t}` : s;
};

// Compute editable status client-side using created_at + window
const canEditNow = (createdAt, windowMin) => {
  if (!createdAt) return false;
  try {
    const t = new Date(createdAt).getTime();
    return (Date.now() - t) / 60000 <= (windowMin || 60);
  } catch { return false; }
};
const minutesLeft = (createdAt, windowMin) => {
  if (!createdAt) return 0;
  try {
    const t = new Date(createdAt).getTime();
    return Math.max(0, Math.ceil((windowMin || 60) - (Date.now() - t) / 60000));
  } catch { return 0; }
};

export default function Sales() {
  const { user, can } = useAuth();
  const BRANCHES = useCabangOptions({ denganKosong: true });
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const canEdit = user?.role !== "viewer";
  // Autocomplete produk memakai modul inventory_produk — modul berbeda dari
  // sales. Kalau tidak diizinkan, kolom nama barang tetap ketik bebas (tidak
  // pernah memanggil endpoint lookup, jadi tidak ada 403 yang mengganggu).
  const bolehLookupProduk = can("inventory_produk", "view");
  // Pemilih IMEI per baris barang (brief bos poin 4: IMEI hanya boleh
  // DIPILIH dari unit yang terdaftar di inventory, bukan diketik bebas).
  // Modul beda lagi dari inventory_produk — kalau tidak diizinkan, baris
  // ber-SKU tetap bisa disimpan TANPA IMEI (backend tidak pernah memaksa
  // IMEI terisi), pemilihnya sekadar tidak muncul.
  const bolehLookupUnit = can("inventory_unit", "view");
  // Cetak faktur/struk (brief #17-19) — template desain sendiri masih dijaga
  // modul "faktur" (Admin), sementara cetak transaksinya mengikuti izin sales
  // yang sudah dipegang siapa pun yang bisa membuka detail nota ini.
  const bolehFaktur = can("faktur", "view");
  // Sumber Info (brief bos 11 Agu 2026) — saklar darurat + pilihan + daftar
  // KOL/PFP aktif diambil SEKALI di sini (bukan tiap modal dibuka/tiap
  // ketikan), lihat docstring useSourceInfoConfig.
  const sourceInfoCfg = useSourceInfoConfig();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [periode, setPeriode] = useState(bulanIniISO());
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [branch, setBranch] = useState("");
  const [modal, setModal] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // create | edit
  const [form, setForm] = useState(emptyForm());
  // Paket/bundling (poin 8, brief bos 23 Agu 2026) — resep AKTIF (dengan stok
  // virtualnya) dimuat ULANG tiap modal dibuka supaya angka stok paket yang
  // dilihat kasir selalu segar (bukan cache basi dari kunjungan sebelumnya).
  // Tanpa `bolehLookupProduk`, resepMap tetap kosong -> baris tetap berjalan
  // seperti barang biasa, sama seperti ItemNamaField kalau lookup produk
  // tidak diizinkan (lihat docstring BundleKomponenPicker.jsx).
  const { resepMap: resepBundlingMap } = useResepBundlingAktif(bolehLookupProduk && modal);
  const { produkInfoCache, ensureProdukInfo } = useProdukInfoCache(bolehLookupProduk);
  // Dialog konfirmasi PPN sebelum simpan (brief bos) — menyimpan payload yang
  // sudah divalidasi, menunggu klik "Lanjutkan". null = tidak tampil.
  const [ppnConfirm, setPpnConfirm] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editWindow, setEditWindow] = useState(60);
  const [saving, setSaving] = useState(false);
  const [draftModal, setDraftModal] = useState(null); // {loading, message, wa_url, no_hp, nama_pelanggan, sale_id}
  // {jenis, step, templates, templateId, loading, data} — step: "memuat_template" |
  // "pilih_template" | "kosong" | "tanpa_izin" | "preview". Lihat bukaCetak()/
  // muatDokumenCetak() di bawah.
  const [cetakModal, setCetakModal] = useState(null);
  // PIN persetujuan hapus (brief #23) — satu dialog dipakai ulang untuk
  // penghapusan nota manapun di halaman ini via `pinDialog.retry(pin)`.
  const [pinDialog, setPinDialog] = useState(null); // {pin, submitting, error, retry}
  // Ajukan retur penjualan (brief bos) — dibuka dari panel detail transaksi;
  // approval sesungguhnya terjadi di halaman /retur (agen lain).
  const [returModal, setReturModal] = useState(null);
  const [kasBankAccounts, setKasBankAccounts] = useState([]);
  // Metode pembayaran (samakan dengan Input Nota, brief bos) — sumber TUNGGAL
  // koleksi `payment_methods` lewat `/payment-methods`, BUKAN lagi daftar
  // ringkas ("Transfer"/"Debit"/dst.) yang ditanam mati di berkas ini. Tanpa
  // ini, penjualan yang diinput langsung di sini (bukan lewat approval Nota)
  // kehilangan info bank ("Transfer BCA" runtuh jadi "Transfer") dan tidak
  // bisa direkonsiliasi per bank di Rekon Sales Harian — lihat
  // `_rekon_channel_dari_metode` (server.py), yang kini memakai nilai metode
  // APA ADANYA sebagai channel.
  const [paymentMethods, setPaymentMethods] = useState([]);   // active-only, untuk dropdown
  const [paymentGroups, setPaymentGroups] = useState({});     // active-only, dikelompokkan (sama shape dgn InputNota.jsx)
  // Layar kelola metode. Halaman INI sebelumnya tidak punya tombolnya sama
  // sekali padahal justru di sini kasir paling sering bertemu metode baru —
  // kalau metodenya belum terdaftar/terpetakan, uangnya jatuh ke Kas 1010.
  const [paymentGroupsAll, setPaymentGroupsAll] = useState({});
  const [methodsModal, setMethodsModal] = useState(false);
  // Jebakan harga_terima efektif (edit nota berdiskon) — openEdit memuat
  // form.harga_terima dari sale.harga_terima, yang untuk nota berdiskon/biaya
  // admin BUKAN ketikan kasir, melainkan nilai EFEKTIF hasil hitung server
  // (subtotal - diskon + admin) saat nota itu masih pakai diskon/admin. Flag
  // ini menandai kondisi tsb supaya save() tahu kapan nilai itu harus
  // diabaikan (lihat pemakaian di `hargaTerimaJebakan` & payload di save()).
  const [hargaTerimaOverride, setHargaTerimaOverride] = useState({ aktif: false, nilaiAwal: 0 });
  // Deep-link ke satu transaksi (brief bos 12 Agu #7) — Pelanggan.jsx menautkan
  // baris riwayat belanja ke `/sales?id=<sale_id>`. Pesan ini menjelaskan KENAPA
  // transaksi tertaut gagal dibuka (dihapus / tak berhak / error lain) supaya
  // pengguna tidak mendarat di halaman kosong dan mengira datanya hilang.
  const [linkedSaleNotice, setLinkedSaleNotice] = useState(null);

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (dateFrom || dateTo) {
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
    } else if (periode) {
      p.set("periode", periode);
    }
    if (branch) p.set("branch", branch);
    api.get(`/sales?${p}`).then(r => setItems(r.data));
  }, [periode, dateFrom, dateTo, branch]);

  useEffect(() => { load(); }, [load]);

  // Rekap trade-in ikut filter yang sama; hanya refetch saat panelnya terbuka.
  const [tradeInData, setTradeInData] = useState(null);
  const [showTradeIn, setShowTradeIn] = useState(false);
  const [loadingTradeIn, setLoadingTradeIn] = useState(false);
  // Pengurutan tabel rekap trade-in (client-side, lihat components/ui/sort.jsx) —
  // urutan bawaan tetap seperti sebelumnya (mengikuti urutan `rows` dari server,
  // yang sudah `.sort("tanggal", -1)`), header cuma jadi opsional untuk pengguna.
  const tradeInSort = useSortState(null, "asc");
  useEffect(() => {
    if (!showTradeIn) return;
    loadTradeIn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periode, dateFrom, dateTo, branch, showTradeIn]);
  useEffect(() => {
    api.get("/employees").then(r => {
      const list = (r.data || []).filter(e => e.is_active !== false)
        .sort((a, b) => a.nama.localeCompare(b.nama));
      setEmployees(list);
    }).catch(() => setEmployees([]));
    api.get("/config").then(r => setEditWindow(r.data?.edit_window_minutes || 60)).catch(() => {});
    loadMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sama persis endpoint & shape yang dipakai InputNota.jsx (active_only=true
  // untuk dropdown form, active_only=false untuk layar kelola) — satu sumber
  // data, bukan cara kedua.
  const loadMethods = () => {
    api.get("/payment-methods?active_only=true").then(r => {
      setPaymentMethods(r.data?.items || []);
      setPaymentGroups(r.data?.groups || {});
    }).catch(() => {});
    api.get("/payment-methods?active_only=false").then(r => {
      setPaymentGroupsAll(r.data?.groups || {});
    }).catch(() => {});
  };

  const totalItems = useMemo(
    () => form.items.reduce((a, x) => a + (parseFloat(x.harga) || 0), 0),
    [form.items]
  );
  // Paket/bundling (poin 8-9, brief bos 23 Agu 2026, aturan #3 CLAUDE.md) —
  // alasan (kalau ada) kenapa tombol Simpan masih ditahan: baris paket yang
  // stoknya habis, komponennya belum lengkap, atau IMEI-nya belum dipilih
  // semua. null = semua baris paket (kalau ada) sudah siap dikirim.
  const bundleAlasanTertahan = useMemo(
    () => cariAlasanBundleBelumSiap(form.items, resepBundlingMap, produkInfoCache),
    [form.items, resepBundlingMap, produkInfoCache]
  );
  const totalSplit = useMemo(
    () => (form.payments || []).reduce((a, p) => a + (parseFloat(p.amount) || 0), 0),
    [form.payments]
  );
  // Nilai unit yang diterima sebagai trade-in ikut MEMBAYAR transaksi. Tanpa
  // memperhitungkannya, nota trade-in selalu tampak "kurang bayar" sebesar nilai
  // unitnya dan penyimpanan diblokir — persis keluhan yang dilaporkan. Rumus ini
  // disamakan dengan InputNota.jsx yang sudah benar.
  const tradeInTotal = useMemo(
    () => (form.trade_in_items || []).reduce((a, t) => a + (parseFloat(t.harga) || 0), 0),
    [form.trade_in_items]
  );
  // Diskon & biaya admin (brief bos) — persen dihitung dari subtotal SETELAH
  // diskon, persis rumus server `_hitung_diskon_biaya_admin` di
  // routers/sales.py, supaya pratinjau di sini tidak pernah menyimpang dari
  // angka yang benar-benar disimpan.
  // N2: diskon = jumlah diskon per barang, atau diskon nota LAMA transaksi yang
  // dibuka (tidak pernah keduanya); biaya admin hanya dari transaksi lama.
  const diskonBarisForm = diskonBarisTotal(form.items);
  const diskonNominal = diskonEfektifForm(form);
  const { mode: biayaAdminMode, nilai: biayaAdminNilai } = adminEfektifForm(form);
  // PPN (keputusan owner 18 Agu 2026) — dpp tingkat DOKUMEN (item yang diketik
  // kasir sudah exclusive PPN). Sejak 25 Sep 2026 (26f) DPP = totalItems SESUDAH
  // diskon (`rincianLayar.dpp`, cermin `pajak_nota` server). Pratinjau saja,
  // lihat PPN_PERSEN_PREVIEW.
  const kenaPpn = !!form.kena_ppn;
  // SATU rincian untuk layar ini — rumus & pembulatan yang SAMA dengan server
  // (lib/rincianBayar.js, dijaga tes kesetaraan JS vs Python). Biaya admin &
  // PPN dulu dihitung di sini dengan `Math.round` (bukan .5-ke-genap seperti
  // server), dan acuan split memakai ketikan Harga Terima. DP hanya ada pada
  // nota yang DIBUKA untuk diedit (dibuat lewat Input Nota); layar ini tidak
  // membuat DP baru.
  const rincianLayar = useMemo(() => rincianFormPenjualan({
    totalBarang: totalItems, diskon: diskonNominal, biayaAdminMode, biayaAdminNilai,
    kenaPpn, ppnPersen: PPN_PERSEN_PREVIEW, tradeIn: tradeInTotal, dp: form._dp_nominal || 0,
  }), [totalItems, diskonNominal, biayaAdminMode, biayaAdminNilai, kenaPpn, tradeInTotal, form._dp_nominal]);
  const biayaAdminRp = rincianLayar.biayaAdmin;
  const ppnPreview = rincianLayar.ppn;
  // Diperluas dari diskon/admin (nama dipertahankan supaya diff minimal di
  // pemakai lain) — PPN memakai gerbang yang SAMA PERSIS karena efeknya
  // sama: memaksa harga_terima efektif dihitung ulang server, bukan dipakai
  // apa adanya dari ketikan kasir. Lihat `_hitung_diskon_biaya_admin` di
  // backend (parameter ppn_nominal) untuk formula lengkapnya.
  const diskonAdminAktif = diskonNominal > 0 || biayaAdminRp > 0 || kenaPpn;
  // Jebakan nilai efektif basi (lihat `hargaTerimaOverride` di atas): saat
  // sedang EDIT nota yang tadinya berdiskon/biaya-admin, dan kasir baru saja
  // menonaktifkan diskon+admin di form ini (`!diskonAdminAktif`) TANPA
  // menyentuh kolom Harga Terima secara manual (nilainya masih persis nilai
  // efektif yang dimuat openEdit), maka form.harga_terima tidak boleh dipakai
  // — itu angka lama yang sudah dipotong diskon yang kini sudah tidak ada.
  // Diperlakukan seolah kosong supaya jatuh ke fallback totalItems, sama
  // seperti kalau kasir memang tidak pernah mengisi Harga Terima manual.
  // Nota trade-in murni (tanpa diskon/admin) tidak pernah mengaktifkan
  // `hargaTerimaOverride.aktif`, jadi cabang ini tidak pernah tersentuh dan
  // rumus lama (`form.harga_terima || totalItems`) berlaku persis seperti
  // sebelumnya.
  const hargaTerimaJebakan = modalMode === "edit" && hargaTerimaOverride.aktif && !diskonAdminAktif
    && Math.abs((form.harga_terima || 0) - hargaTerimaOverride.nilaiAwal) < 0.5;
  // Nota BERTEMPO: `harga_terima` kosong berarti BELUM DIBAYAR, bukan "anggap
  // lunas". Fallback `|| totalItems` dibuat untuk nota TUNAI lama; memakainya
  // pada nota bertempo membuat `uang_diterima_nota` mengembalikan nilai penuh
  // → `total_payments` = total → piutangnya dibatalkan sebagai "lunas di
  // tempat" dan `repost_sale_to_ledger` mendebit kas yang tidak pernah
  // diterima siapa pun. Nol galat, nol log. (Temuan tinjauan 12 Sep 2026;
  // aturan yang sama sudah tertulis di `routers/sales.py::uang_diterima_nota`
  // dan di `nota_module.py` — layar ini satu-satunya yang belum mengikutinya.)
  const notaBertempo = !!(form.tempo_pembayaran || "").trim();
  // Tinjauan gelombang 3 putaran 2 (aturan 3 CLAUDE.md): nota LAMA (tanpa
  // penanda pembayaran per baris) tidak boleh BERPINDAH ke bentuk bertempo +
  // trade-in lewat edit — server menolaknya 409 (`update_sale`). Dokumen mana
  // yang terkena ditentukan server (`_tempo_tradein_terkunci`, penentu yang SAMA
  // dengan penjaganya); layar hanya menahan KOMBINASINYA: centang/nilai trade-in
  // mati pada nota bertempo, pilihan tempo mati pada nota ber-trade-in, dengan
  // alasan dari server. Dulu keduanya tampil dan Simpan berujung 409.
  const t2Terkunci = modalMode === "edit" && !form._bayar_per_baris && !!form._tempo_tradein_terkunci;
  const t2TradeinMati = t2Terkunci && notaBertempo;
  const t2TempoMati = t2Terkunci && tradeInTotal > 0.005;
  const t2Ditahan = t2Terkunci && notaBertempo && tradeInTotal > 0.005;
  // Petunjuk Kredit Retur (I1, 22 Sep 2026): hanya saat MEMBUAT nota — edit
  // menyembunyikan Kredit Retur (server 409). Satu sumber dengan Input Nota.
  const kreditInfo = useKreditRetur(form.nama_pelanggan, form.no_hp, modal && modalMode === "create");
  const hargaTerima = hargaTerimaJebakan
    ? totalItems
    : (notaBertempo ? (Number(form.harga_terima) || 0) : (form.harga_terima || totalItems));
  // "Total harus dibayar" dan acuan Split Payment.
  // Nota TUNAI (tinjauan 24 Sep 2026): dari rincian komponen nota — ketikan
  // Harga Terima TIDAK ikut. Dulu tanpa diskon/admin/PPN acuannya `hargaTerima
  // − tradeIn`; begitu kasir menyalin placeholder (yang sudah dikurangi
  // trade-in), trade-in terkurang DUA KALI, split dipaksa sebesar itu, dan
  // server menyimpan uang kurang tanpa bisa diposting. Acuan split = uang di
  // kasir (harus dibayar − DP), sama dengan server (split + DP = harus dibayar)
  // dan layar Input Nota.
  // Nota BERTEMPO: rumus lama dipertahankan apa adanya — Harga Terima di sana
  // adalah uang muka, dan perilaku bertempo di luar lingkup N0.
  const totalHarusDibayar = (notaBertempo && !diskonAdminAktif)
    ? hargaTerima - tradeInTotal
    : rincianLayar.harusDibayar;
  const acuanSplit = notaBertempo ? totalHarusDibayar : rincianLayar.diTempat;
  const splitBalance = acuanSplit - totalSplit;
  // Blok pembayaran berbaris (batch N3) — hanya form bertanda (transaksi baru
  // dan transaksi yang lahir dari layar N3). Baris UTAMA nota tunai mengikuti
  // sisa tagihan dari rincian yang sama dengan server sampai kasir
  // mengetiknya sendiri; nota bertempo tidak pernah diikutkan (uang muka).
  useEffect(() => {
    if (!form._bayar_per_baris) return;
    setForm(s => {
      const baru = ikutkanSisa(s.payments, { diTempat: rincianLayar.diTempat,
                                             tempo: !!(s.tempo_pembayaran || "").trim() });
      return baru === s.payments ? s : { ...s, payments: baru };
    });
  }, [form._bayar_per_baris, rincianLayar.diTempat, form.tempo_pembayaran, form.payments]);
  const galatBayar = form._bayar_per_baris
    ? galatBarisPembayaran(form.payments, rincianLayar, { tempo: notaBertempo }) : null;
  // Nota DOWNGRADE bertanda (tinjauan 1 N3): selisihnya ditransfer Finance, jadi
  // rekening pelanggan wajib — aturan & pesan cermin server.
  const downgradeBayar = form._bayar_per_baris
    ? ringkasBlokPembayaran(rincianLayar, form.payments, { tempo: notaBertempo }).downgrade : false;
  // Tinjauan 2 N3: nota bertanda yang TERSIMPAN bertempo dijadikan tunai =
  // piutangnya dibatalkan dan uang dicatat SUDAH diterima di kasir. Server
  // menolaknya tanpa pernyataan bernominal (`periksa_pelunasan_tempo`); layar
  // memintanya lewat kotak centang yang terikat pada angka yang sedang tampil —
  // angka berubah sesudah dicentang = centangnya gugur.
  const lunasTempo = modalMode === "edit" && !!form._bayar_per_baris && !!form._boleh_edit_tempo
    && !!(form._tempo_awal || "").trim() && !notaBertempo;
  const uangLunasTempo = lunasTempo
    ? ringkasBlokPembayaran(rincianLayar, form.payments, { tempo: false }).uangKasir : 0;
  const lunasDinyatakan = lunasTempo && form._pelunasan_tempo_nominal != null
    && Math.abs(Number(form._pelunasan_tempo_nominal) - uangLunasTempo) < 0.005;
  // Tunai -> bertempo lewat blok F5 (tinjauan 24 Sep 2026): nota yang DIBUKA
  // sebagai tunai menyimpan nilai LUNAS di Harga Terima, bukan uang muka.
  // Membiarkannya di kolom membuat SPV mengira uang muka sebesar itu sudah
  // diterima; server kini membacanya sebagai 0 bila tidak disentuh, dan layar
  // mengosongkannya supaya yang terlihat sama dengan yang dicatat. Kembali ke
  // tunai sebelum disimpan = angka semula dipulihkan.
  // N1 (brief 22 Sep 2026) — identitas pelanggan, CERMIN server
  // (`routers/identitas_nota.py`): Cabang & No HP wajib saat transaksi BARU
  // atau saat medannya DIUBAH (nota lama tanpa HP tetap bisa diedit selama
  // HP-nya tidak disentuh), nama B2B/USER bertempo wajib dari kartu pelanggan.
  const aturanIdentitas = useAturanIdentitasNota();
  const identitasAwal = modalMode === "edit" ? form._identitas_awal : null;
  const hpBerubah = !identitasAwal || hpBerubahLib(identitasAwal.no_hp, form.no_hp);
  const cabangBerubah = !identitasAwal
    || (identitasAwal.branch || "").trim().toLowerCase() !== (form.branch || "").trim().toLowerCase();
  const cekHp = periksaBentukHp(form.no_hp);
  const galatHp = !hpBerubah ? null
    : (!cekHp.sah ? cekHp.pesan
      : (!cekHp.simpan && aturanIdentitas.hp_cabang_wajib
        ? "No HP pelanggan wajib diisi (HP Indonesia 10–13 angka, contoh 0812 3456 7890)." : null));
  // Kolom kosong tidak langsung dimerahkan; kewajibannya disampaikan saat Simpan.
  const galatHpTampil = (form.no_hp || "").trim() ? galatHp : null;
  const galatCabang = cabangBerubah && aturanIdentitas.hp_cabang_wajib && !(form.branch || "").trim()
    ? "Cabang wajib dipilih." : null;
  const perluKartu = butuhKartu(form, identitasAwal, aturanIdentitas);
  // Memilih kartu: nama = ejaan kartu. No HP kartu hanya mengisi kolom yang
  // masih kosong — pelanggan B2B boleh memakai nomor milik kartu lain.
  const pilihKartu = (k) => setForm(f => {
    const hpKartu = hpTampilan(k.no_hp);
    const isiHp = !(f.no_hp || "").trim() && hpKartu && periksaBentukHp(hpKartu).simpan;
    return { ...f, nama_pelanggan: k.nama, _kartu: { nama: k.nama }, no_hp: isiHp ? hpKartu : f.no_hp };
  });

  const gantiTempo = (nilai) => setForm(s => {
    if (s._bayar_per_baris) {
      // N3: baris utama nota BERTEMPO = uang muka opsional (0); kembali tunai =
      // mengikuti sisa lagi. Harga Terima tidak dipakai form bertanda.
      // Tinjauan 2: nota yang TERSIMPAN bertempo dijadikan tunai → baris utama 0
      // (tidak diisi otomatis) dan pernyataan pelunasan diulang dari nol.
      const berubah = !!nilai !== !!(s.tempo_pembayaran || "").trim();
      return { ...s, tempo_pembayaran: nilai, _pelunasan_tempo_nominal: null,
               payments: berubah
                 ? aturBarisUtamaTempo(s.payments, !!nilai, { dariTempoTersimpan: !!(s._tempo_awal || "").trim() })
                 : s.payments };
    }
    if ((s._tempo_awal || "").trim()) return { ...s, tempo_pembayaran: nilai };
    const sebelumnya = (s.tempo_pembayaran || "").trim();
    if (nilai && !sebelumnya) return { ...s, tempo_pembayaran: nilai, harga_terima: 0, _ht_disentuh: true };
    if (!nilai && sebelumnya) return { ...s, tempo_pembayaran: nilai, harga_terima: s._ht_awal || 0, _ht_disentuh: false };
    return { ...s, tempo_pembayaran: nilai };
  });

  const updItem = (i, k, v) => setForm(s => {
    const ni = [...s.items];
    ni[i] = { ...ni[i], [k]: (k === "harga" || k === "diskon_rp") ? (parseFloat(v) || 0) : v };
    // Barang bonus tidak bisa berdiskon (server menolaknya) — ikut dinolkan.
    if (k === "is_bonus" && v) ni[i].diskon_rp = 0;
    // Auto-derive kode dari kata pertama nama_barang. Kode tidak ditampilkan ke user.
    // Kategori ikut disarankan ulang HANYA sebagai cadangan teks bebas (baris
    // tanpa SKU) — baris ber-SKU tidak pernah lewat jalur ini, lihat
    // ItemNamaField.pilih() yang memakai kategori master produk apa adanya.
    if (k === "nama_barang") { ni[i].kode = deriveKode(v); ni[i].kategori = deriveKategoriTeksBebas(v); }
    return { ...s, items: ni };
  });
  // Tempel beberapa field item sekaligus — dipakai autocomplete produk supaya
  // nama_barang, sku, harga, dan kategori terisi dalam satu update (tidak
  // saling menimpa lewat beberapa panggilan updItem berurutan).
  const patchItem = (i, patch) => setForm(s => {
    const ni = [...s.items];
    ni[i] = { ...ni[i], ...patch };
    return { ...s, items: ni };
  });
  const addItem = () => setForm(s => ({
    ...s, items: [...s.items, { nama_barang: "", harga: 0, kode: "HP", kategori: KATEGORI_DEFAULT, sku: "", imei: "", is_bonus: false, komponen: null, diskon_rp: 0 }],
  }));
  const rmItem = (i) => setForm(s => ({ ...s, items: s.items.filter((_, x) => x !== i) }));

  const updPay = (i, k, v) => setForm(s => {
    const np = [...(s.payments || [])];
    np[i] = { ...np[i], [k]: k === "amount" ? (parseFloat(v) || 0) : v };
    return { ...s, payments: np };
  });
  const addPay = () => setForm(s => ({
    ...s,
    payments: [...(s.payments || []), { method: paymentMethods[0]?.name || "Cash", amount: Math.max(0, splitBalance) }],
  }));
  const rmPay = (i) => setForm(s => ({ ...s, payments: (s.payments || []).filter((_, x) => x !== i) }));

  // Blok trade-in form (satu sumber untuk form bertanda — di dalam blok
  // pembayaran, D5 — dan form lama).
  const renderTradeIn = () => (
    <>
      <F l="Trade-in?">
        <div className="flex items-center gap-4 h-10">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={form.trade_in}
              disabled={t2TradeinMati && !form.trade_in}
              onChange={e => setForm(s => ({
                ...s, trade_in: e.target.checked,
                // Centang pertama langsung menyiapkan satu baris unit supaya
                // kasir tidak menghadapi area kosong tanpa petunjuk.
                trade_in_items: e.target.checked && !(s.trade_in_items || []).length
                  ? [{ nama: "", harga: 0, imei: "" }] : s.trade_in_items,
              }))}
              data-testid="sale-tradein" /> Ya
          </label>
          {form.trade_in && (
            <input value={form.trade_in_note}
              onChange={e => setForm(s => ({ ...s, trade_in_note: e.target.value }))}
              placeholder="catatan trade-in (opsional)"
              className="flex-1 h-8 px-2 rounded border border-slate-200 text-xs" />
          )}
        </div>
      </F>
      {t2TradeinMati && (
        <p className="md:col-span-2 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
          data-testid="sale-t2-alasan-tradein">
          {form._tempo_tradein_alasan}
        </p>
      )}
      {/* Unit trade-in: dulu halaman ini HANYA punya kotak catatan, jadi
          nota trade-in yang dibuat lewat Input Nota tidak bisa diedit di
          sini tanpa kehilangan unitnya. Form-nya disamakan dengan
          InputNota — nama, IMEI, dan nilai per unit. */}
      {form.trade_in && (
        <div className="md:col-span-2 border border-violet-200 bg-violet-50/40 rounded-lg p-3 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-violet-800">Unit yang diterima (trade-in)</p>
            <p className="text-xs text-violet-800">Total: <b>{idr(tradeInTotal)}</b></p>
          </div>
          {(form.trade_in_items || []).map((t, i) => (
            <div key={i} className="grid grid-cols-12 gap-2">
              <input value={t.nama} data-testid={`tradein-name-${i}`}
                onChange={e => setForm(s => {
                  const a = [...(s.trade_in_items || [])]; a[i] = { ...a[i], nama: e.target.value };
                  return { ...s, trade_in_items: a };
                })}
                placeholder="Nama unit (mis. iPhone 12 128GB)"
                className="col-span-5 h-9 px-2 rounded border border-slate-200 text-sm" />
              <input value={t.imei} data-testid={`tradein-imei-${i}`}
                onChange={e => setForm(s => {
                  const a = [...(s.trade_in_items || [])]; a[i] = { ...a[i], imei: e.target.value };
                  return { ...s, trade_in_items: a };
                })}
                placeholder="IMEI / SN"
                className="col-span-4 h-9 px-2 rounded border border-slate-200 text-sm font-mono" />
              <input type="number" value={t.harga || ""} data-testid={`tradein-price-${i}`}
                disabled={t2TradeinMati}
                onChange={e => setForm(s => {
                  const a = [...(s.trade_in_items || [])]; a[i] = { ...a[i], harga: parseFloat(e.target.value) || 0 };
                  return { ...s, trade_in_items: a };
                })}
                placeholder="Nilai"
                className="col-span-2 h-9 px-2 rounded border border-slate-200 text-sm" />
              <button type="button" onClick={() => setForm(s => ({
                ...s, trade_in_items: (s.trade_in_items || []).filter((_, x) => x !== i),
              }))} className="col-span-1 h-9 rounded border border-slate-200 text-slate-400 hover:text-rose-600 hover:bg-rose-50 text-sm">×</button>
            </div>
          ))}
          <button type="button" data-testid="tradein-add"
            onClick={() => setForm(s => ({
              ...s, trade_in_items: [...(s.trade_in_items || []), { nama: "", harga: 0, imei: "" }],
            }))}
            className="h-8 px-3 rounded-lg border border-violet-300 bg-white hover:bg-violet-50 text-xs font-medium text-violet-800">
            + Tambah unit trade-in
          </button>
        </div>
      )}
    </>
  );


  const openCreate = () => {
    setModalMode("create");
    setForm(emptyForm());
    setHargaTerimaOverride({ aktif: false, nilaiAwal: 0 });
    setModal(true);
  };

  const openEdit = (sale) => {
    setModalMode("edit");
    // Tandai jebakan nilai efektif SEBELUM setForm: sale.harga_terima yang
    // akan dimuat ke form di bawah adalah hasil hitungan server (subtotal -
    // diskon + admin) kalau nota ini sedang pakai diskon dan/atau biaya
    // admin — lihat komentar panjang di `hargaTerimaJebakan`.
    setHargaTerimaOverride({
      aktif: (sale.diskon_nominal || 0) > 0 || (sale.biaya_admin_rp || 0) > 0 || (sale.ppn_nominal || 0) > 0,
      nilaiAwal: sale.harga_terima || 0,
    });
    setForm({
      id: sale.id,
      tanggal: sale.tanggal || hariIniISO(),
      waktu: sale.waktu || nowTime(),
      nama_pelanggan: sale.nama_pelanggan || "",
      no_hp: sale.no_hp || "",
      // F5 (brief owner 12 Sep 2026) — Jenis Pelanggan & Metode Pembayaran/
      // Tempo boleh diedit pada nota B2B/USER bertempo. `_boleh_edit_tempo`
      // datang dari server (routers/sales.py::get_sale), BUKAN ditebak layar:
      // ia sudah memperhitungkan izin akun DAN apakah piutangnya sudah
      // menerima pembayaran.
      _boleh_edit_tempo: !!sale._boleh_edit_tempo,
      // Putaran 2: nota lama yang tidak boleh berpindah ke bertempo + trade-in —
      // penanda & alasannya dari server (`get_sale`), bukan ditebak layar.
      _tempo_tradein_terkunci: !!sale._tempo_tradein_terkunci,
      _tempo_tradein_alasan: sale._tempo_tradein_alasan || "",
      jenis_pelanggan: sale.jenis_pelanggan || "umum",
      tempo_pembayaran: sale.tempo_pembayaran || "",
      pic_approval_piutang: sale.pic_approval_piutang || "",
      // Pemetaan baris barang dokumen -> form SATU sumber dengan Input Nota
      // (`lib/payloadNota.js::itemsFormDariDokumen`): SKU, IMEI, bonus,
      // komponen paket, dan diskon per barang (N2). Baris SINTETIK per komponen
      // paket (`is_bundle_component`, dibangun backend) disaring di sana —
      // kasir akan mengira ada barang gratis nyasar dan menghapusnya, merusak
      // komposisi paket. Kategori transaksi lama yang kosong ditebak dari teks.
      items: itemsFormDariDokumen(
        sale.items && sale.items.length ? sale.items : [{ nama_barang: "", harga: 0, kode: "HP" }],
        deriveKategoriTeksBebas),
      imei: sale.imei || "",
      metode_pembayaran: sale.metode_pembayaran || "Cash",
      trade_in: !!sale.trade_in,
      trade_in_note: sale.trade_in_note || "",
      // WAJIB ikut dimuat: dulu daftar unit trade-in tidak pernah dibaca ke form
      // dan juga tidak dikirim ulang saat menyimpan, sehingga sekali nota
      // trade-in diedit, unit + IMEI-nya lenyap dari basis data.
      // Sub-kolom kondisi unit (status/warna/BH/kelengkapan/catatan) WAJIB
      // ikut dimuat: server mewajibkannya setiap kali nota trade-in disimpan
      // (`validasi_trade_in_wajib`), jadi versi lama yang hanya memuat
      // nama/harga/IMEI membuat SETIAP edit nota trade-in di layar ini 400.
      trade_in_items: (sale.trade_in_items || []).map(t => ({
        ...t, nama: t.nama || t.nama_barang || "", harga: t.harga || 0, imei: t.imei || "",
      })),
      harga_terima: sale.harga_terima || 0,
      // N0: Harga Terima hanya dikirim ulang kalau kasir MENYENTUH kolomnya.
      // Mengirim ulang angka tersimpan apa adanya membuat server tidak bisa
      // membedakan "tidak disentuh" dari "diketik sama".
      _ht_disentuh: false,
      // Potret saat dibuka — dipakai `gantiTempo` (tunai -> bertempo) dan
      // petunjuk bertempo -> tunai. DP tidak bisa diubah lewat edit; ia hanya
      // ikut menentukan uang di kasir (acuan split).
      _tempo_awal: sale.tempo_pembayaran || "",
      _ht_awal: sale.harga_terima || 0,
      _dp_nominal: sale.dp_nominal || 0,
      catatan: sale.catatan || "",
      nama_penjual: sale.nama_penjual || "",
      branch: sale.branch || "",
      // N2: PIC diskon satu per nota; diskon nota & biaya admin LAMA hanya
      // potret baca-saja — payload TIDAK mengirimnya (server mempertahankan
      // nilai tersimpan), kecuali kasir menekan "Hapus diskon lama".
      diskon_pic_approval: sale.diskon_pic_approval || "",
      _diskon_lama: ambilDiskonLama(sale),
      // Nota lama (sebelum fitur PPN ada) tidak punya field ini -> default
      // false, kompatibel mundur.
      kena_ppn: !!sale.kena_ppn,
      is_split: !!(sale.payments && sale.payments.length > 1),
      // N3: dokumen BERTANDA dibuka dengan blok pembayaran berbaris (keterangan
      // & metode_id ikut — tanpa itu keterangan hilang begitu disimpan ulang);
      // dokumen LAMA tetap form lama persis (split/Harga Terima, skema 2).
      _bayar_per_baris: sale.bayar_per_baris === true,
      ...rekeningPengembalian(sale),
      _rekening_terkunci: sale.bayar_per_baris === true && rekeningLengkap(rekeningPengembalian(sale)),
      payments: sale.bayar_per_baris === true
        ? barisPembayaranDariDokumen(sale)
        : (sale.payments && sale.payments.length
          ? sale.payments.map(p => ({ method: p.method, amount: p.amount }))
          : [{ method: sale.metode_pembayaran || "Cash", amount: sale.harga_terima || 0 }]),
      // Transaksi lama (sebelum fitur Sumber Info ada, 11 Agu 2026) tidak
      // punya field ini -> "" (kosong). Backend TETAP mempertahankan nilai
      // lama kalau PATCH ini tidak menyentuhnya sama sekali (lihat komentar
      // `eff_source_info` di routers/sales.py update_sale) — tapi form ini
      // SELALU mengirim field-nya (lihat save()), jadi mengosongkan di sini
      // berarti kasir harus memilih ulang sebelum menyimpan perubahan apa pun,
      // konsisten dengan pagar hard-stop yang sama dipakai transaksi baru.
      source_info: sale.source_info || "", source_ref_id: sale.source_ref_id || "",
      // N1: potret identitas saat dibuka — "wajib" hanya untuk medan yang
      // DIUBAH (cermin server), jadi nota lama tanpa HP tetap bisa diedit.
      _kartu: null,
      _identitas_awal: {
        nama_pelanggan: sale.nama_pelanggan || "", no_hp: sale.no_hp || "", branch: sale.branch || "",
        jenis_pelanggan: sale.jenis_pelanggan || "umum", tempo_pembayaran: sale.tempo_pembayaran || "",
      },
    });
    setDetail(null); setDetailId(null);
    setModal(true);
  };

  const save = async () => {
    if (!form.nama_pelanggan || !form.nama_penjual) { toast.error("Nama pelanggan & penjual wajib"); return; }
    // Identitas pelanggan (N1) — cermin server, dicegat sebelum Simpan.
    if (galatCabang) { toast.error(galatCabang); return; }
    if (galatHp) { toast.error(galatHp); return; }
    if (perluKartu && !form._kartu) {
      toast.error("Nota B2B/USER bertempo: pilih nama pelanggan dari daftar Data Pelanggan, "
        + "atau tekan \"+ Pelanggan baru\" kalau belum ada.");
      return;
    }
    if (form.items.some(x => !x.nama_barang)) { toast.error("Nama barang tiap item wajib"); return; }
    // Sumber Info (brief bos 11 Agu 2026) — cermin persis aturan backend
    // (_validasi_dan_lengkapi_source_info) supaya kasir dicegat DI SINI,
    // bukan menabrak 400 dari server setelah menekan "Simpan".
    const errSourceInfo = validasiSourceInfo(form.source_info, form.source_ref_id, sourceInfoCfg);
    if (errSourceInfo) { toast.error(errSourceInfo); return; }
    // Backend menolak baris berharga Rp 0 kecuali ditandai bonus (brief bos) —
    // dicegat di sini dulu supaya kasir tidak menabrak error 400 dari server
    // tanpa tahu jalan keluarnya.
    const itemGratisTanpaTanda = form.items.find(x => x.nama_barang && (parseFloat(x.harga) || 0) === 0 && !x.is_bonus);
    if (itemGratisTanpaTanda) {
      toast.error(`Harga "${itemGratisTanpaTanda.nama_barang}" masih Rp0 — centang "Bonus/Gratis" kalau barang ini memang diberikan cuma-cuma, atau isi harga jualnya.`);
      return;
    }
    // Satu unit tidak boleh terpilih di dua baris sekaligus DALAM SATU
    // transaksi ini (backend TIDAK menegakkan ini — `_tolak_stok_kosong`
    // memvalidasi tiap baris independen, lihat catatan di ItemImeiField).
    // Ini pagar terakhir sebelum terkirim; pemilihnya sendiri sudah
    // menonaktifkan opsi yang sama di baris lain, jadi ini seharusnya jarang
    // tersentuh.
    const imeiTerpakai = form.items.map(x => (x.imei || "").trim()).filter(Boolean);
    const imeiDobel = imeiTerpakai.find((v, idx) => imeiTerpakai.indexOf(v) !== idx);
    if (imeiDobel) {
      toast.error(`IMEI ${imeiDobel} dipilih di lebih dari satu baris — satu unit hanya boleh dijual sekali dalam satu transaksi.`);
      return;
    }
    // Paket/bundling (poin 8-9, brief bos 23 Agu 2026) — aturan #3 CLAUDE.md:
    // jangan biarkan kasir menekan Simpan lalu menabrak 400 dari backend.
    // Tombol Simpan sudah ditahan (lihat `disabled` di bawah) untuk kasus
    // yang SUDAH terlihat sebelum submit; cek ini jaring pengaman terakhir
    // (mis. race saat stok/produkInfoCache berubah tepat sebelum klik).
    const alasanBundle = cariAlasanBundleBelumSiap(form.items, resepBundlingMap, produkInfoCache);
    if (alasanBundle) { toast.error(alasanBundle); return; }
    if (t2Ditahan) { toast.error(form._tempo_tradein_alasan); return; }
    // Diskon per barang (N2) — cermin aturan server, pesan yang sama.
    const galatDiskon = galatDiskonBaris(form.items) || galatCampurDiskon(form);
    if (galatDiskon) { toast.error(galatDiskon); return; }
    if (diskonBarisForm > 0 && !(form.diskon_pic_approval || "").trim()) {
      toast.error("Diskon per barang wajib menyebut PIC yang menyetujuinya"); return;
    }

    const payload = {
      tanggal: form.tanggal,
      waktu: form.waktu || nowTime(),
      nama_pelanggan: form.nama_pelanggan,
      no_hp: form.no_hp,
      // `diskon_rp` eksplisit di SETIAP baris (lib/payloadNota.js).
      items: itemsKePayload(form.items),
      imei: form.imei,
      metode_pembayaran: form.metode_pembayaran,
      trade_in: form.trade_in,
      trade_in_note: form.trade_in_note,
      trade_in_items: form.trade_in_items || [],
      // Batch UANG-KASIR N0 (23 Sep 2026): yang dikirim PERSIS ketikan kasir
      // (0 = kosong), bukan lagi `form.harga_terima || totalItems` — fallback
      // itu mengirim DPP/bruto yang tidak pernah bermakna dan kini ditolak
      // server. Uangnya dihitung server dari komponen nota; angka ini hanya
      // konfirmasi. Nota bertempo: uang muka yang benar-benar diterima.
      harga_terima: Number(form.harga_terima) || 0,
      harga_terima_diketik: true,
      catatan: form.catatan,
      nama_penjual: form.nama_penjual,
      branch: form.branch,
      // Bagian diskon (N2): penanda layar `skema_nota`, PIC, dan diskon nota/
      // biaya admin LAMA hanya bila kasir menghapusnya — satu sumber dengan
      // Input Nota (lib/payloadNota.js `bagianDiskonPayload`).
      ...bagianDiskonPayload(form, "sales"),
      // PPN (keputusan owner 18 Agu 2026) — server yang menghitung
      // ppn_persen/ppn_nominal/dpp sesungguhnya, ini cuma togglenya.
      kena_ppn: kenaPpn,
      // Sumber Info / atribusi marketing (brief bos 11 Agu 2026) — sudah
      // divalidasi client-side di atas (validasiSourceInfo), server tetap
      // menegakkan ulang (_validasi_dan_lengkapi_source_info).
      source_info: form.source_info || null,
      source_ref_id: form.source_ref_id || null,
    };

    // F5: ketiga medan tempo HANYA dikirim saat blok editnya memang tampil.
    // Kalau selalu dikirim, setiap edit nota tunai biasa ikut membawa
    // `jenis_pelanggan: "umum"` dan `tempo_pembayaran: null` — dan PATCH ini
    // memakai `exclude_unset`, jadi mengirimnya berarti MENIMPA nilai yang
    // ada. Nota B2B yang diedit kasir lewat jalur lain akan kehilangan
    // temponya tanpa ada yang menekan apa pun.
    if (modalMode === "edit" && form._boleh_edit_tempo) {
      payload.jenis_pelanggan = form.jenis_pelanggan || "umum";
      payload.tempo_pembayaran = form.tempo_pembayaran || null;
      payload.pic_approval_piutang = (form.pic_approval_piutang || "").trim() || null;
    }
    // Edit: Harga Terima yang TIDAK disentuh tidak dikirim (lihat `_ht_disentuh`
    // di openEdit). Server lalu menghitung ulang dari rincian bila uangnya
    // berubah, atau tidak menulis medan uang sama sekali bila tidak.
    if (modalMode === "edit" && !form._ht_disentuh) {
      delete payload.harga_terima;
    }
    // Kredit Retur hanya sah saat nota DIBUAT (server menolak 409 saat edit).
    const krDiEdit = form._bayar_per_baris
      ? (form.payments || []).some(p => adalahKreditReturMetode(p.method) && (Number(p.amount) || 0) > 0)
      : ((!form.is_split && adalahKreditRetur(form.metode_pembayaran))
        || (form.is_split && (form.payments || []).some(p => adalahKreditRetur(p.method))));
    if (modalMode === "edit" && krDiEdit) {
      toast.error("Kredit Retur hanya bisa dipakai saat nota dibuat — hapus lalu buat ulang nota ini.");
      return;
    }

    if (form._bayar_per_baris) {
      // N3: blok pembayaran berbaris — aturan yang SAMA dengan server
      // (`terapkan_baris_pembayaran`): keterangan baris tambahan, metode dari
      // daftar, Σ baris + DP = yang harus dibayar (tunai), bertempo tidak
      // dibayar penuh. Payload: `skema_nota` 3 + baris (satu sumber dengan
      // Input Nota, lib/payloadNota.js). Total Pembayaran kasir TIDAK diminta
      // di layar ini — pembuat nota di sini adalah pemeriksanya sendiri.
      if (galatBayar) { toast.error(galatBayar); return; }
      const galatRek = form._rekening_terkunci ? null
        : galatRekeningPengembalian(form, { downgrade: downgradeBayar });
      if (galatRek) { toast.error(galatRek); return; }
      if (lunasTempo && !lunasDinyatakan) {
        toast.error(`Nota ini tadinya bertempo (${form._tempo_awal}). Centang "Pelanggan sudah melunasi `
          + `${idr(uangLunasTempo)} di kasir" untuk menyatakan pelunasannya, atau biarkan tetap bertempo `
          + "dan catat pembayarannya lewat menu Piutang.");
        return;
      }
      Object.assign(payload, bagianBayarPayload(form),
        bagianRekeningPayload(form, { kirim: downgradeBayar && !form._rekening_terkunci }));
      if (lunasTempo) payload.pelunasan_tempo_nominal = uangLunasTempo;
    } else if (form.is_split) {
      const pays = (form.payments || []).filter(p => p.method && (parseFloat(p.amount) || 0) > 0);
      if (pays.length === 0) { toast.error("Isi minimal 1 baris split payment"); return; }
      const sum = pays.reduce((a, p) => a + p.amount, 0);
      // Acuan split sudah memperhitungkan trade-in, diskon/admin/PPN, dan DP —
      // lihat definisi `acuanSplit` di atas.
      const wajibTunai = acuanSplit;
      if (Math.abs(wajibTunai - sum) > 0.5) {
        const rincian = (diskonAdminAktif || !notaBertempo)
          ? ` (barang ${idr(totalItems)}` +
            (diskonNominal > 0 ? ` − diskon ${idr(diskonNominal)}` : "") +
            (biayaAdminRp > 0 ? ` + admin ${idr(biayaAdminRp)}` : "") +
            (kenaPpn ? ` + PPN ${idr(ppnPreview)}` : "") +
            (tradeInTotal > 0 ? ` − trade-in ${idr(tradeInTotal)}` : "") +
            (!notaBertempo && rincianLayar.dp > 0 ? ` − DP ${idr(rincianLayar.dp)}` : "") + ")"
          : (tradeInTotal > 0 ? ` (harga ${idr(hargaTerima)} − trade-in ${idr(tradeInTotal)})` : "");
        toast.error(`Total pembayaran ${idr(sum)} belum menutup ${idr(wajibTunai)}${rincian}`);
        return;
      }
      payload.payments = pays;
      payload.metode_pembayaran = pays.length === 1 ? pays[0].method : "Split";
    }

    // PPN (brief bos): dialog konfirmasi sebelum simpan — supaya kasir sadar
    // transaksi ini akan menambahkan kewajiban pajak (2400 PPN Keluaran)
    // sebelum benar-benar terkirim.
    if (kenaPpn) {
      setPpnConfirm(payload);
      return;
    }
    await doSaveSale(payload);
  };

  const doSaveSale = async (payload) => {
    setSaving(true);
    try {
      let res;
      if (modalMode === "edit" && form.id) {
        res = await api.patch(`/sales/${form.id}`, payload);
        toast.success("Perubahan tersimpan");
      } else {
        res = await api.post("/sales", payload);
        toast.success("Penjualan tercatat");
      }
      // Peringatan non-blokir "dijual di bawah modal" (brief bos 21 Agu #B1,
      // gantikan price floor per kategori yang dicabut) — transaksi TETAP
      // tersimpan (lihat toast.success di atas), ini murni alarm, pola sama
      // dengan `peringatan_harga` di InventoryPO.jsx. SENGAJA tidak menyebut
      // angka modal (keputusan privasi owner) — hanya nama/SKU barangnya.
      (res?.data?.peringatan_di_bawah_modal || []).forEach(p =>
        toast.warning(`${p.nama_barang || p.sku} dijual di bawah modal`, { duration: 8000 }));
      // Posting ke buku besar yang gagal TIDAK lagi senyap (N0).
      if (res?.data?.posting_ledger?.status === "gagal") {
        toast.warning(`Transaksi tersimpan, tetapi BELUM masuk buku besar: ${res.data.posting_ledger.alasan || "-"} `
          + "Finance melihatnya di Audit › Nota Belum Terposting.", { duration: 12000 });
      }
      setModal(false);
      setForm(emptyForm());
      setPpnConfirm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal");
    } finally {
      setSaving(false);
    }
  };

  const doDeleteSale = async (id, pin) => {
    await api.delete(`/sales/${id}`, pin ? { params: { pin } } : undefined);
    setDetailId(null); setDetail(null);
    load();
  };

  // Brief #23 — DELETE bisa membalas 428 (butuh PIN persetujuan). Alurnya:
  // ajukan PIN otomatis lewat /aktivitas/hapus/ajukan, lalu buka dialog PIN
  // supaya pengguna tinggal menempelkan kode yang diberikan pemberi izin.
  const del = async (id) => {
    if (!confirm("Yakin hapus penjualan ini?")) return;
    try {
      await doDeleteSale(id);
    } catch (e) {
      if (e?.response?.status === 428) {
        try {
          await api.post("/aktivitas/hapus/ajukan", {
            modul: "sales", target_id: id,
            ringkasan: `Hapus penjualan ${detail?.nama_pelanggan || ""}`.trim(),
          });
        } catch (e2) {
          toast.error(e2?.response?.data?.detail || "Gagal mengajukan izin hapus");
          return;
        }
        setPinDialog({ pin: "", submitting: false, error: "", retry: (pin) => doDeleteSale(id, pin) });
      } else {
        toast.error(e?.response?.data?.detail || "Gagal menghapus penjualan");
      }
    }
  };

  const submitPinDialog = async () => {
    if (!pinDialog) return;
    setPinDialog(d => ({ ...d, submitting: true, error: "" }));
    try {
      await pinDialog.retry(pinDialog.pin);
      setPinDialog(null);
    } catch (e) {
      if (e?.response?.status === 403) {
        setPinDialog(d => ({ ...d, submitting: false, error: "PIN salah atau sudah kedaluwarsa — coba lagi." }));
      } else {
        toast.error(e?.response?.data?.detail || "Gagal menghapus");
        setPinDialog(d => ({ ...d, submitting: false }));
      }
    }
  };

  // ---- Ajukan Retur Penjualan (brief bos) — dibuka dari panel detail
  // transaksi; approval sesungguhnya terjadi di /retur (agen lain). ----
  // Default nominal retur — prorata NETO, bukan bruto. `r.harga` per baris
  // adalah harga JUAL asli (belum dipotong diskon nota), jadi menjumlahkannya
  // apa adanya menyarankan refund lebih besar dari uang yang benar-benar
  // diterima untuk nota berdiskon (over-refund). `faktorNeto` = (total_harga
  // − diskon_nominal) / total_harga meratakan proporsi diskon ke tiap baris.
  // `faktorPpn` (18 Agu 2026, brief PPN) menambahkan porsi PPN nota ini
  // secara proporsional — nota ber-PPN membuat pelanggan membayar DPP+PPN,
  // jadi saran refund yang berhenti di DPP saja akan selalu kurang dari uang
  // yang sungguh diterima (lihat `_neto_diterima_sale` di backend, satu
  // sumber kebenaran yang sama dipakai cap `nominal_kembali` DAN pembagian
  // proporsional jurnal saat approve). Hasilnya di-clamp ke `sisaNilaiMaks`
  // (sisa hak retur nota ini setelah dikurangi retur aktif lain, JUGA sudah
  // mencakup PPN) supaya saran tidak pernah melebihi itu. Ini HANYA
  // memengaruhi nominal SARAN — harga per baris yang dikirim ke backend
  // (`submitRetur`) tetap index_item/qty/alasan, tidak berubah.
  // Rumusnya kini di `lib/retur.js` (`hitungNominalReturDefault`) — dipakai
  // juga oleh menu "Ajukan Retur Penjualan" di halaman Retur (P18a, 4 Sep 2026).

  const openReturModal = async (sale) => {
    setReturModal({
      loading: true, submitting: false, saleId: sale.id,
      // Identitas nota — menentukan boleh-tidaknya tukar unit (I1, 22 Sep 2026).
      saleNama: sale.nama_pelanggan || "", saleNoHp: sale.no_hp || "",
      // Tempo & status tagihan dari SERVER (29a, 25 Sep 2026) — nota bertempo
      // yang belum lunas tidak boleh ditukar unit.
      saleTempo: sale.tempo_pembayaran || "",
      // no. 33: medan baru dulu, nama lama sebagai cadangan (respons lama).
      saleTagihanTukar: sale._retur_tagihan || sale._tukar_unit_tagihan || null,
      items: [], nominal_kembali: 0, nominalTouched: false, akun: "", catatan: "",
      faktorNeto: 1, faktorPpn: 0, sisaNilaiMaks: 0, diskonNominal: sale.diskon_nominal || 0,
      kenaPpn: !!sale.kena_ppn, ppnNominalSale: sale.kena_ppn ? (sale.ppn_nominal || 0) : 0,
    });
    try {
      // GET /retur/penjualan butuh izin retur.view — pemegang retur.create
      // SAJA (tanpa view) akan kena 403 di panggilan ini. Itu bukan alasan
      // modal ini gagal dibuka: daftar retur aktif di sini cuma dipakai
      // sebagai BATAS UX (qty/nominal maksimum), server tetap validator akhir
      // saat POST. Jadi 403 khusus panggilan ini diperlakukan sebagai "belum
      // ada retur aktif" (list kosong), bukan error yang menutup modal.
      const [kbRes, existingRes] = await Promise.all([
        kasBankAccounts.length ? Promise.resolve({ data: kasBankAccounts }) : api.get("/finance/accounts/kas-bank"),
        api.get("/retur/penjualan", { params: { sale_id: sale.id } })
          .catch(e => {
            if (e?.response?.status === 403) return { data: { items: [] } };
            throw e;
          }),
      ]);
      if (!kasBankAccounts.length) setKasBankAccounts(kbRes.data || []);
      // Baris retur + faktor prorata disusun oleh SATU sumber (lib/retur.js,
      // `susunReturDariNota`) — sama persis dengan yang dipakai halaman Retur.
      // Penjelasan soal isi paket/komponen ada di sana.
      const susun = susunReturDariNota(sale, existingRes.data?.items || []);
      setReturModal(m => ({
        ...m, loading: false, items: susun.rows, faktorNeto: susun.faktorNeto, faktorPpn: susun.faktorPpn,
        sisaNilaiMaks: susun.sisaNilaiMaks, diskonNominal: susun.diskonNominal,
        kenaPpn: susun.kenaPpn, ppnNominalSale: susun.ppnNominalSale,
        diskonPerBaris: susun.diskonPerBaris,
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat data retur");
      setReturModal(null);
    }
  };

  const toggleReturItem = (i, checked) => setReturModal(m => {
    const items = m.items.map((r, idx) => idx === i
      ? { ...r, checked, qtyRetur: checked ? (r.qtyRetur || Math.min(1, r.sisa)) : r.qtyRetur }
      : r);
    return {
      ...m, items,
      nominal_kembali: m.nominalTouched ? m.nominal_kembali
        : hitungNominalReturDefault(items, m.faktorNeto, m.faktorPpn, m.sisaNilaiMaks),
    };
  });
  const setReturQty = (i, v) => setReturModal(m => {
    const batas = m.items[i]?.sisa || 0;
    const qty = Math.max(0, Math.min(batas, parseInt(v, 10) || 0));
    const items = m.items.map((r, idx) => idx === i ? { ...r, qtyRetur: qty } : r);
    return {
      ...m, items,
      nominal_kembali: m.nominalTouched ? m.nominal_kembali
        : hitungNominalReturDefault(items, m.faktorNeto, m.faktorPpn, m.sisaNilaiMaks),
    };
  });
  const setReturAlasan = (i, v) => setReturModal(m => ({
    ...m, items: m.items.map((r, idx) => idx === i ? { ...r, alasan: v } : r),
  }));
  // Tukar unit hanya untuk nota ber-nama & ber-No HP sah (I1): kreditnya hanya
  // bisa dipakai pelanggan dengan nama DAN No HP yang sama. Aturannya SATU
  // sumber (lib/retur.js), sama dengan menu Ajukan Retur di halaman Retur.
  const _notaReturModal = returModal
    ? { nama_pelanggan: returModal.saleNama, no_hp: returModal.saleNoHp,
      tempo_pembayaran: returModal.saleTempo, _retur_tagihan: returModal.saleTagihanTukar }
    : null;
  const syaratTukarModal = returModal ? syaratTukarUnit(_notaReturModal) : null;
  // no. 33 (Bos 19 Okt 2026): tagihan bertempo yang belum lunas menahan retur
  // UANG juga — satu sumber rumus yang sama dengan halaman Retur.
  const tagihanReturModal = returModal ? syaratTagihanRetur(_notaReturModal) : null;

  const submitRetur = async () => {
    if (!returModal) return;
    const dipilih = returModal.items.filter(r => r.checked && r.qtyRetur > 0);
    if (dipilih.length === 0) { toast.error("Pilih minimal 1 barang untuk diretur"); return; }
    if (dipilih.some(r => !r.alasan.trim())) { toast.error("Alasan retur wajib diisi tiap barang yang dipilih"); return; }
    // Akun kas hanya wajib untuk pengembalian UANG. "Tukar unit" tidak
    // mengeluarkan uang sama sekali hari itu — nilainya ditahan jadi kredit
    // milik pelanggan.
    if (returModal.jenisKembali !== "tukar_unit" && !returModal.akun) {
      toast.error("Pilih akun pengembalian uang"); return;
    }
    // no. 33: ditahan untuk KEDUA bentuk pengembalian.
    if (tagihanReturModal?.ditahan) { toast.error(tagihanReturModal.alasan); return; }
    if (returModal.jenisKembali === "tukar_unit" && !syaratTukarModal?.boleh) {
      toast.error(syaratTukarModal?.alasan || "Nota ini tidak bisa diretur tukar unit"); return;
    }
    if (!(returModal.nominal_kembali > 0)) { toast.error("Nominal kembali harus lebih dari 0"); return; }
    setReturModal(m => ({ ...m, submitting: true }));
    try {
      const { data } = await api.post("/retur/penjualan", {
        sale_id: returModal.saleId,
        items: dipilih.map(r => ({ index_item: r.index, qty: r.qtyRetur, alasan: r.alasan.trim() })),
        nominal_kembali: returModal.nominal_kembali,
        jenis_kembali: returModal.jenisKembali || "uang",
        metode_kembali: returModal.jenisKembali === "tukar_unit" ? "" : returModal.akun,
        catatan: returModal.catatan || "",
      });
      toast.success(data.message || "Retur penjualan diajukan — menunggu approval");
      setReturModal(null);
      setDetailId(null); setDetail(null);
      // Pemegang retur.create SAJA (tanpa retur.view) tidak bisa membuka
      // /retur — halaman itu digerbang modul "retur" view. Untuk mereka,
      // cukup tutup modal; toast di atas (dari `data.message`) sudah
      // menyebut nomor pengajuan yang menunggu approval.
      if (can("retur", "view")) navigate("/retur");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengajukan retur");
      setReturModal(m => ({ ...m, submitting: false }));
    }
  };

  // Cetak faktur/struk (brief #17-19 + pemilih template) — satu alur untuk
  // dua tombol ("Cetak Faktur"/"Cetak Struk"): pilih JENIS dulu lewat tombol
  // mana yang ditekan, lalu (kalau ada lebih dari satu template untuk jenis
  // itu) pilih TEMPLATE-nya. Tombol tidak pernah hilang diam-diam lagi — kalau
  // belum ada template sama sekali, modal menjelaskan itu alih-alih tombolnya
  // disembunyikan begitu saja.
  const bukaCetak = async (jenisCetak) => {
    if (!detail) return;
    setCetakModal({ jenis: jenisCetak, step: "memuat_template", templates: [], templateId: null, loading: true, data: null });
    if (!bolehFaktur) {
      // Tidak punya izin menelusuri daftar template ("faktur.view") — untuk
      // faktur, biarkan backend memilih default jenis "penjualan" seperti
      // biasa. Untuk struk, tidak ada cara menemukan template struk tanpa
      // izin ini, jadi jelaskan alih-alih memaksakan/menyembunyikan tombol.
      if (jenisCetak === "struk") {
        setCetakModal({ jenis: jenisCetak, step: "tanpa_izin", templates: [], loading: false, data: null });
        return;
      }
      await muatDokumenCetak(jenisCetak, null);
      return;
    }
    try {
      const { data } = await api.get("/faktur/templates", { params: { jenis: jenisCetak } });
      const daftar = data?.items || [];
      if (daftar.length === 0) {
        setCetakModal({ jenis: jenisCetak, step: "kosong", templates: [], loading: false, data: null });
        return;
      }
      if (daftar.length === 1) {
        await muatDokumenCetak(jenisCetak, daftar[0]);
        return;
      }
      const def = daftar.find(t => t.is_default) || daftar[0];
      setCetakModal({ jenis: jenisCetak, step: "pilih_template", templates: daftar, templateId: def.id, loading: false, data: null });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat daftar template cetak");
      setCetakModal(null);
    }
  };

  const muatDokumenCetak = async (jenisCetak, template) => {
    setCetakModal(m => ({ ...(m || { jenis: jenisCetak }), jenis: jenisCetak, step: "preview", loading: true, data: null }));
    try {
      // Struk berbagi satu entitas sale yang sama dengan faktur. Backend
      // /faktur/data/penjualan (lihat faktur.py::_pilih_template +
      // _JENIS_SETARA) sekarang menerima template_id berjenis "struk" untuk
      // dokumen "penjualan" selama dipilih EKSPLISIT seperti di sini, jadi
      // template_id cukup dikirim apa adanya — tidak perlu lagi menimpa
      // `template` di sisi klien seperti sebelumnya.
      const { data } = await api.get(`/faktur/data/penjualan/${detail.id}`, {
        params: template?.id ? { template_id: template.id } : {},
      });
      setCetakModal(m => (m ? { ...m, step: "preview", loading: false, data } : m));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat dokumen cetak");
      setCetakModal(null);
    }
  };

  const kirimFakturWA = async () => {
    if (!detail) return;
    try {
      const { data } = await api.post(`/faktur/penjualan/${detail.id}/kirim-wa`);
      if (data.ok) {
        toast.success("Faktur terkirim otomatis via WhatsApp");
      } else if (data.wa_link) {
        window.open(data.wa_link, "_blank");
        toast.success("Tautan faktur siap — WhatsApp terbuka");
      } else {
        toast.error("Tautan tidak terbentuk — cek no. HP pelanggan");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim faktur via WhatsApp");
    }
  };

  const openDetail = async (id) => {
    setDetailId(id); setDetail(null);
    try {
      const { data } = await api.get(`/sales/${id}`);
      setDetail(data);
    } catch { toast.error("Gagal memuat detail"); setDetailId(null); }
  };

  // Deep-link ke satu transaksi (brief bos 12 Agu #7) — dipanggil dari
  // `?id=` di URL. Diambil LANGSUNG dari API by id (bukan dicari di `items`,
  // yang cuma berisi halaman/filter periode yang sedang aktif), jadi transaksi
  // tetap terbuka walau ada di bulan/cabang lain di luar filter saat ini.
  // Kalau gagal, tampilkan alasan yang jelas lewat `linkedSaleNotice` — beda
  // dari `openDetail` biasa (toast singkat), karena tautan ini sering dibuka
  // sebagai satu-satunya aksi pengguna di halaman ini, dan toast bisa terlewat.
  const openLinkedSale = useCallback(async (id) => {
    setDetailId(id); setDetail(null); setLinkedSaleNotice(null);
    try {
      const { data } = await api.get(`/sales/${id}`);
      setDetail(data);
    } catch (e) {
      setDetailId(null); setDetail(null);
      const status = e?.response?.status;
      setLinkedSaleNotice(
        status === 404
          ? "Transaksi yang Anda tuju tidak ditemukan — kemungkinan sudah dihapus."
          : status === 403
          ? "Anda tidak berhak melihat transaksi ini."
          : (e?.response?.data?.detail || "Gagal memuat transaksi yang dituju. Coba lagi.")
      );
    } finally {
      // Bersihkan `?id=` dari URL setelah dicoba (berhasil maupun gagal) —
      // supaya refresh/tombol Kembali browser tidak mengulang percobaan yang
      // sama, dan supaya menutup lalu membuka modal lain tidak "menyalakan
      // ulang" tautan lama.
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.delete("id");
        return next;
      }, { replace: true });
    }
  }, [setSearchParams]);

  const linkedSaleId = searchParams.get("id");
  useEffect(() => {
    if (linkedSaleId) openLinkedSale(linkedSaleId);
  }, [linkedSaleId, openLinkedSale]);

  const draftWA = async (sale) => {
    setDraftModal({ loading: true, message: "", wa_url: "", no_hp: sale.no_hp || "", nama_pelanggan: sale.nama_pelanggan || "", sale_id: sale.id });
    try {
      const { data } = await api.post("/ai/draft-wa", { sale_id: sale.id });
      setDraftModal({ loading: false, ...data, sale_id: sale.id });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal generate draft");
      setDraftModal(null);
    }
  };

  const regenDraft = async () => {
    if (!draftModal?.sale_id) return;
    setDraftModal(m => ({ ...m, loading: true }));
    try {
      const { data } = await api.post("/ai/draft-wa", { sale_id: draftModal.sale_id });
      setDraftModal({ loading: false, ...data, sale_id: draftModal.sale_id });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal regenerate");
      setDraftModal(m => ({ ...m, loading: false }));
    }
  };

  const copyDraft = async () => {
    if (!draftModal?.message) return;
    try {
      await navigator.clipboard.writeText(draftModal.message);
      toast.success("Draft disalin!");
    } catch { toast.error("Gagal salin"); }
  };

  const exportExcel = async () => {
    const p = new URLSearchParams();
    if (periode) p.set("periode", periode);
    if (branch) p.set("branch", branch);
    const token = localStorage.getItem("hris_token");
    const res = await fetch(`${API}/sales/export?${p}`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Sales_${periode}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  // ---- Rekap trade-in (transaksi yang mencentang trade-in) ----
  const tradeInParams = () => {
    const p = new URLSearchParams();
    if (dateFrom || dateTo) {
      if (dateFrom) p.set("date_from", dateFrom);
      if (dateTo) p.set("date_to", dateTo);
    } else if (periode) {
      p.set("periode", periode);
    }
    if (branch) p.set("branch", branch);
    return p;
  };

  const loadTradeIn = async () => {
    setLoadingTradeIn(true);
    try {
      const { data } = await api.get(`/sales/trade-in?${tradeInParams()}`);
      setTradeInData(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal memuat rekap trade-in");
    } finally {
      setLoadingTradeIn(false);
    }
  };

  const toggleTradeIn = () => {
    const next = !showTradeIn;
    setShowTradeIn(next);
    if (next) loadTradeIn();
  };

  const exportTradeIn = async () => {
    const token = localStorage.getItem("hris_token");
    const res = await fetch(`${API}/sales/trade-in/export?${tradeInParams()}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) { toast.error("Gagal export rekap trade-in"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Rekap_TradeIn_${dateFrom || dateTo ? `${dateFrom || "awal"}_sd_${dateTo || "akhir"}` : periode}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success("Rekap trade-in sedang diunduh");
  };

  const exportDaily = async () => {
    const p = new URLSearchParams();
    if (periode) p.set("periode", periode);
    if (branch) p.set("branch", branch);
    const token = localStorage.getItem("hris_token");
    const res = await fetch(`${API}/export/sales-daily?${p}`, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) { toast.error("Gagal export"); return; }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `Sales_Daily_${periode}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success("Export per-hari (31 sheet) sedang diunduh");
  };

  const totalOmzet = items.reduce((a, s) => a + (s.harga_terima || s.total_harga || 0), 0);

  const branchEmployees = form.branch
    ? employees.filter(e => !e.branch || e.branch === form.branch)
    : employees;

  // Pengaman isian belum tersimpan (brief owner) — form Input/Edit Penjualan
  // adalah form terpanjang di halaman ini (item + trade-in + IMEI, menjurnal +
  // potong stok), dan Ajukan Retur punya qty + alasan per baris. Hook ini
  // dipanggil di level komponen (bukan di dalam JSX kondisional
  // `{modal && ...}`), jadi `active` WAJIB diikat ke flag buka/tutup modalnya
  // sendiri supaya status "dirty" & konfirmasi ikut reset tiap modal dibuka
  // ulang (lihat docstring `active` di hooks/useModalGuard.jsx).
  const saleGuard = useModalGuard({
    onClose: () => setModal(false), onSubmit: save, busy: saving, dangerous: true, active: modal,
  });
  const returGuard = useModalGuard({
    onClose: () => setReturModal(null), onSubmit: submitRetur,
    busy: !!(returModal?.loading || returModal?.submitting), dangerous: true, active: !!returModal,
  });
  // Dialog PPN (hand-rolled) terbuka DI ATAS modal Input Penjualan ini —
  // daftarkan sebagai lapisan lebih atas supaya Escape yang ditujukan untuk
  // dialog PPN tidak ikut memicu palang "ada isian belum disimpan" milik
  // saleGuard di baliknya (lihat useModalGuard.jsx).
  useModalLayer(!!ppnConfirm);

  return (
    <div className="p-4 md:p-8 max-w-[1400px]">
      <header className="flex items-start justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600 mb-2">Penjualan</p>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Input Penjualan</h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">
            <Info className="w-3.5 h-3.5 inline mb-0.5 mr-1 text-slate-400" />
            Klik <b>kartu penjualan</b> untuk melihat detail. Setelah simpan, Anda bisa <b>edit</b> dalam <b>{editWindow} menit</b>.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={async () => {
            const token = localStorage.getItem("hris_token");
            const res = await fetch(`${API}/import-templates/sales`, { headers: { Authorization: `Bearer ${token}` } });
            if (!res.ok) { toast.error("Gagal download template"); return; }
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = "Template_Sales.xlsx";
            document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
          }} data-testid="btn-template-sales"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-sm font-medium">
            <FileText className="w-4 h-4" /> Template
          </button>
          {canEdit && (
            <label data-testid="btn-import-sales"
              className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium cursor-pointer">
              <Upload className="w-4 h-4" /> Import
              <input type="file" accept=".xlsx,.xls" className="hidden"
                onChange={async (e) => {
                  const f = e.target.files?.[0]; if (!f) return;
                  const fd = new FormData(); fd.append("file", f);
                  try {
                    const { data } = await api.post("/sales/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                    toast.success(`Import: ${data.created} baru${data.errors.length ? `, ${data.errors.length} error` : ""}`);
                    load();
                  } catch (err) { toast.error(err?.response?.data?.detail || "Gagal import"); }
                  e.target.value = "";
                }} />
            </label>
          )}
          <button onClick={exportExcel} data-testid="btn-export-sales"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium">
            <Download className="w-4 h-4" /> Excel
          </button>
          <button onClick={exportDaily} data-testid="btn-export-sales-daily"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-sm font-medium">
            <Download className="w-4 h-4" /> Export Harian (Per Sheet)
          </button>
          <button onClick={toggleTradeIn} data-testid="btn-toggle-tradein-recap"
            className={`inline-flex items-center gap-2 h-10 px-4 rounded-lg border text-sm font-medium transition-colors ${
              showTradeIn
                ? "border-violet-400 bg-violet-100 text-violet-900"
                : "border-violet-300 bg-violet-50 hover:bg-violet-100 text-violet-800"
            }`}>
            <Repeat className="w-4 h-4" /> Rekap Trade-in
          </button>
          {bolehKelolaMetode(user) && (
            <TombolKelolaMetode jumlah={paymentMethods.length} onClick={() => setMethodsModal(true)} />
          )}
          {canEdit && (
            <button onClick={openCreate} data-testid="btn-add-sale"
              className="hidden">+</button>
          )}
        </div>
      </header>

      {methodsModal && (
        <MetodePembayaranManager
          groups={paymentGroupsAll}
          onClose={() => setMethodsModal(false)}
          onChange={loadMethods}
        />
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm mb-6 flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Periode Bulan</span>
          <MonthPicker value={periode} onChange={v => { setPeriode(v); setDateFrom(""); setDateTo(""); }}
            data-testid="sales-periode"
            className="mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Dari Tanggal</span>
          <DatePicker value={dateFrom} onChange={setDateFrom}
            data-testid="sales-date-from"
            className="mt-1" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Sampai Tanggal</span>
          <DatePicker value={dateTo} onChange={setDateTo}
            data-testid="sales-date-to"
            className="mt-1" />
        </label>
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(""); setDateTo(""); }}
            data-testid="sales-reset-range"
            className="h-10 px-3 rounded-lg border border-slate-200 text-xs text-slate-600 hover:bg-slate-50">
            Reset Range
          </button>
        )}
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Branch</span>
          <select value={branch} onChange={e => setBranch(e.target.value)}
            className="mt-1 h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white min-w-[180px]">
            {BRANCHES.map(b => <option key={b} value={b}>{b || "Semua Branch"}</option>)}
          </select>
        </label>
        <div className="ml-auto flex gap-6 text-right">
          <div>
            <p className="text-xs text-slate-500">Transaksi</p>
            <p className="font-display text-2xl font-bold text-slate-900">{items.length}</p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Omzet</p>
            <p className="font-display text-2xl font-bold text-emerald-700">{idr(totalOmzet)}</p>
          </div>
        </div>
      </div>

      {showTradeIn && (
        <div className="bg-white border border-violet-200 rounded-xl shadow-sm mb-6 overflow-hidden"
          data-testid="tradein-recap-panel">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-violet-100 bg-violet-50/60">
            <div>
              <p className="font-display font-semibold text-slate-900 inline-flex items-center gap-2">
                <Repeat className="w-4 h-4 text-violet-700" /> Rekap Transaksi Trade-in
              </p>
              <p className="text-xs text-slate-500 mt-0.5">Mengikuti filter periode &amp; branch di atas.</p>
            </div>
            <button onClick={exportTradeIn} data-testid="btn-export-tradein"
              className="inline-flex items-center gap-2 h-9 px-3 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium transition-colors">
              <Download className="w-4 h-4" /> Export Excel
            </button>
          </div>

          <LoadingState
            loading={loadingTradeIn}
            kosong={!loadingTradeIn && (!tradeInData || tradeInData.rows.length === 0)}
            pesanKosong="Tidak ada transaksi trade-in pada filter ini."
            data-testid="tradein-recap-empty"
          >
            {tradeInData && (
              <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-slate-100">
                {[
                  ["Transaksi", tradeInData.summary.n_transaksi, "text-slate-900"],
                  ["Unit Trade-in", tradeInData.summary.n_unit_trade_in, "text-violet-700"],
                  ["Nilai Trade-in", idr(tradeInData.summary.total_nilai_trade_in), "text-violet-700"],
                  ["Omzet Transaksi", idr(tradeInData.summary.total_omzet), "text-emerald-700"],
                ].map(([label, value, tone]) => (
                  <div key={label} className="bg-white px-4 py-3">
                    <p className="text-xs text-slate-500">{label}</p>
                    <p className={`font-display text-lg font-bold ${tone}`}>{value}</p>
                  </div>
                ))}
              </div>

              {tradeInData.summary.unit_tanpa_imei > 0 && (
                <p className="px-4 py-2 text-xs text-amber-800 bg-amber-50 border-b border-amber-200"
                  data-testid="tradein-missing-imei-warning">
                  {tradeInData.summary.unit_tanpa_imei} unit trade-in belum punya IMEI. Lengkapi lewat menu Input Nota agar mudah dilacak.
                </p>
              )}

              <TabelDesktop>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      <SortTh label="Tanggal" sortKeyName="tanggal" sortKey={tradeInSort.sortKey} sortDir={tradeInSort.sortDir} onSort={tradeInSort.toggle} testid="tradein-sort-tanggal" />
                      <SortTh label="Pelanggan" sortKeyName="nama_pelanggan" sortKey={tradeInSort.sortKey} sortDir={tradeInSort.sortDir} onSort={tradeInSort.toggle} testid="tradein-sort-pelanggan" />
                      <th className="text-left py-2.5 px-4">Barang Dijual</th>
                      <th className="text-left py-2.5 px-4">Unit Trade-in &amp; IMEI</th>
                      <SortTh label="Nilai Trade-in" sortKeyName="nilai_trade_in" sortKey={tradeInSort.sortKey} sortDir={tradeInSort.sortDir} onSort={tradeInSort.toggle} align="right" testid="tradein-sort-nilai" />
                      <SortTh label="Penjual" sortKeyName="nama_penjual" sortKey={tradeInSort.sortKey} sortDir={tradeInSort.sortDir} onSort={tradeInSort.toggle} testid="tradein-sort-penjual" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortRows(tradeInData.rows, tradeInSort.sortKey, tradeInSort.sortDir).map(r => (
                      <tr key={r.sale_id} className="border-t border-slate-100 hover:bg-slate-50"
                        data-testid={`tradein-row-${r.sale_id}`}>
                        <td className="py-2.5 px-4 text-xs text-slate-700 whitespace-nowrap">
                          {r.tanggal}<span className="block text-[10px] text-slate-400">{r.waktu}</span>
                        </td>
                        <td className="py-2.5 px-4 text-xs text-slate-900 font-medium">{r.nama_pelanggan || "-"}</td>
                        <td className="py-2.5 px-4 text-xs text-slate-700">{r.barang_dijual || "-"}</td>
                        <td className="py-2.5 px-4">
                          {r.trade_in_units.length === 0 ? (
                            <span className="text-xs italic text-slate-400">tanpa rincian</span>
                          ) : r.trade_in_units.map((u, i) => (
                            <div key={i} className="text-xs text-slate-800">
                              {u.nama || <span className="italic text-slate-400">tanpa nama</span>}
                              {u.imei
                                ? <span className="ml-1 font-mono text-[10px] text-violet-700">{u.imei}</span>
                                : <span className="ml-1 text-[10px] text-amber-600">IMEI kosong</span>}
                            </div>
                          ))}
                        </td>
                        <td className="py-2.5 px-4 text-right font-mono text-xs font-semibold text-violet-800">
                          {idr(r.nilai_trade_in)}
                        </td>
                        <td className="py-2.5 px-4 text-xs text-slate-700">{r.nama_penjual || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TabelDesktop>

              <DaftarKartuMobile testid="tradein-recap-kartu">
                {sortRows(tradeInData.rows, tradeInSort.sortKey, tradeInSort.sortDir).map(r => (
                  <KartuBaris key={r.sale_id} testid={`tradein-row-${r.sale_id}-m`}>
                    <KartuBarisHeader
                      kiri={<>
                        <p className="text-sm font-semibold text-slate-900">{r.nama_pelanggan || "-"}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{r.tanggal} · {r.waktu}</p>
                      </>}
                      kanan={<span className="font-mono text-sm font-semibold text-violet-800">{idr(r.nilai_trade_in)}</span>}
                    />
                    <KartuBarisMeta label="Barang Dijual" value={r.barang_dijual || "-"} />
                    <KartuBarisMeta label="Penjual" value={r.nama_penjual || "-"} />
                    <div className="text-xs">
                      <span className="text-slate-500">Unit Trade-in &amp; IMEI</span>
                      {r.trade_in_units.length === 0 ? (
                        <p className="italic text-slate-400 mt-0.5">tanpa rincian</p>
                      ) : r.trade_in_units.map((u, i) => (
                        <div key={i} className="text-slate-800 mt-0.5">
                          {u.nama || <span className="italic text-slate-400">tanpa nama</span>}
                          {u.imei
                            ? <span className="ml-1 font-mono text-[10px] text-violet-700">{u.imei}</span>
                            : <span className="ml-1 text-[10px] text-amber-600">IMEI kosong</span>}
                        </div>
                      ))}
                    </div>
                  </KartuBaris>
                ))}
              </DaftarKartuMobile>
              </>
            )}
          </LoadingState>
        </div>
      )}

      {items.length === 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-16 text-center">
          <MessageCircle className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="font-display font-semibold text-slate-900">Belum ada transaksi di periode ini</p>
          <p className="text-sm text-slate-500 mt-1">Klik &quot;Input Penjualan Baru&quot; untuk mulai mencatat.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map(s => {
            const editable = canEditNow(s.created_at, editWindow);
            const editCount = s.edit_count || 0;
            return (
              <button
                type="button"
                key={s.id}
                data-testid={`sale-${s.id}`}
                onClick={() => openDetail(s.id)}
                className="text-left bg-white border border-slate-200 hover:border-emerald-400 hover:shadow-md transition-all rounded-xl p-5 shadow-sm relative group focus:outline-none focus:ring-2 focus:ring-emerald-500/40"
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-display font-semibold text-slate-900 group-hover:text-emerald-700">{s.nama_pelanggan}</p>
                    <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                      <Phone className="w-3 h-3" /> {s.no_hp || "-"}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500 font-mono">{fmtDateTime(s.tanggal, s.waktu)}</p>
                    <p className="text-xs text-emerald-700 font-medium">{s.branch || "-"}</p>
                  </div>
                </div>
                <div className="space-y-1 mb-3 pb-3 border-b border-slate-100">
                  {s.items?.slice(0, 3).map((it, i) => (
                    <div key={i} className="flex justify-between text-sm">
                      <span className="text-slate-700 truncate mr-2">{it.nama_barang}</span>
                      <span className="font-mono text-slate-900 whitespace-nowrap">{idr(it.harga)}</span>
                    </div>
                  ))}
                  {s.items?.length > 3 && (
                    <p className="text-xs text-slate-400">+{s.items.length - 3} barang lain</p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-wrap text-xs mb-3">
                  {/* Nota bertempo (brief owner 22 Sep 2026): chip metode tunggal
                      ("Transfer BCA") terbaca "sudah dibayar lewat BCA" padahal
                      uangnya menyusul lewat Piutang Usaha. Chip Tempo
                      menggantikannya; split tetap tampil karena itu memang uang
                      yang diterima di tempat. */}
                  {s._bertempo && (
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1"
                      data-testid={`sale-tempo-${s.id}`} title="Nota bertempo — pembayaran menyusul lewat Piutang Usaha">
                      <Clock className="w-3 h-3" /> Tempo {s._tempo_label}
                    </span>
                  )}
                  {s.payments && s.payments.length > 1 ? (
                    <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 inline-flex items-center gap-1"
                      data-testid={`sale-split-${s.id}`}>
                      <Split className="w-3 h-3" /> Split ({s.payments.length})
                    </span>
                  ) : !s._bertempo && (
                    <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 inline-flex items-center gap-1">
                      <CreditCard className="w-3 h-3" /> {s.metode_pembayaran}
                    </span>
                  )}
                  {s.trade_in && (
                    <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 inline-flex items-center gap-1">
                      <Repeat className="w-3 h-3" /> Trade-in
                    </span>
                  )}
                  {s.n_items > 1 && (
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                      {s.n_items} item
                    </span>
                  )}
                  {editCount > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-800 inline-flex items-center gap-1"
                      data-testid={`sale-editmark-${s.id}`} title={`Sudah diedit ${editCount} kali`}>
                      <Pencil className="w-3 h-3" /> {editCount}×
                    </span>
                  )}
                </div>
                <div className="flex items-end justify-between pt-2">
                  <div>
                    <p className="text-xs text-slate-500">Penjual</p>
                    <p className="text-sm font-medium text-slate-900">{s.nama_penjual}</p>
                  </div>
                  {/* Angka dari server (`_total_tampil`, routers/sales.py
                      `total_tampil_nota`): nota bertempo = nilai sesudah diskon,
                      nota tunai = harga_terima efektif seperti dulu. Layar TIDAK
                      menghitung "total − diskon" sendiri (dijaga tes
                      test_total_setelah_diskon_b3.py). */}
                  <p className="font-display font-bold text-emerald-700" data-testid={`sale-total-${s.id}`}>{idr(s._total_tampil)}</p>
                </div>
                {/* Quick action: Draft Balasan AI.
                    Gerbang fitur AI (brief owner 30 Agu 2026) — kunci
                    `ai_assistant`, sama dengan backend /ai/draft-wa. */}
                {bolehPakaiAI(user, can) && (
                <div className="mt-3 pt-2 border-t border-slate-100">
                  <button
                    onClick={(e) => { e.stopPropagation(); draftWA(s); }}
                    data-testid={`sale-draft-wa-${s.id}`}
                    className="w-full inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-lg border border-emerald-200 bg-emerald-50/60 hover:bg-emerald-100 text-emerald-700 text-xs font-medium">
                    <Sparkles className="w-3.5 h-3.5" /> Draft Balasan AI
                  </button>
                </div>
                )}
                {/* Corner edit / lock icon (informational, clickable via detail).
                    Nota bertempo tidak ikut jendela 60 menit (F5) — hitung
                    mundurnya maupun "Terkunci" sama-sama berbohong di sana;
                    status edit yang benar ada di Detail (`_boleh_edit_tempo`). */}
                {canEdit && !s._bertempo && (
                  <div className="absolute top-3 right-3 flex gap-1 items-center">
                    {editable ? (
                      <span data-testid={`sale-editable-${s.id}`}
                        className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {minutesLeft(s.created_at, editWindow)}m
                      </span>
                    ) : (
                      <span data-testid={`sale-locked-${s.id}`}
                        className="text-[10px] text-slate-400 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5 inline-flex items-center gap-1">
                        <Lock className="w-3 h-3" /> Terkunci
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Deep-link gagal dibuka (brief bos 12 Agu #7) — tautan dari Pelanggan.jsx
          yang menunjuk transaksi terhapus/tak berhak/error, supaya pengguna
          tidak mendarat di halaman kosong tanpa penjelasan. */}
      {linkedSaleNotice && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setLinkedSaleNotice(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}
            data-testid="sale-linked-notfound">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h3 className="font-display font-semibold text-slate-900">Transaksi Tidak Bisa Dibuka</h3>
                <p className="text-sm text-slate-600 mt-1">{linkedSaleNotice}</p>
              </div>
            </div>
            <button onClick={() => setLinkedSaleNotice(null)} data-testid="sale-linked-notfound-close"
              className="w-full h-10 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium">
              Tutup
            </button>
          </div>
        </div>
      )}

      {/* DETAIL MODAL */}
      {detailId && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={() => { setDetailId(null); setDetail(null); }}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}
            data-testid="sale-detail-modal">
            <div className="sticky top-0 bg-slate-900 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                <h3 className="font-display font-semibold">Detail Penjualan</h3>
              </div>
              <button onClick={() => { setDetailId(null); setDetail(null); }}
                className="p-2 hover:bg-white/20 rounded-lg" data-testid="sale-detail-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            {!detail ? (
              <div className="p-6">
                <LoadingState loading jumlahKartu={1} data-testid="sale-detail-loading" />
              </div>
            ) : (
              <div className="p-6 space-y-5">
                {/* Header */}
                <div className="flex items-start justify-between border-b border-slate-100 pb-4">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Pelanggan</p>
                    <p className="font-display text-lg font-bold text-slate-900">{detail.nama_pelanggan}</p>
                    <p className="text-sm text-slate-500 mt-1"><Phone className="w-3.5 h-3.5 inline mr-1" />{detail.no_hp || "-"}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-500 uppercase tracking-wider">Waktu</p>
                    <p className="font-mono text-sm text-slate-900">{fmtDateTime(detail.tanggal, detail.waktu)}</p>
                    <p className="text-xs text-emerald-700 font-medium mt-1">{detail.branch || "-"}</p>
                  </div>
                </div>

                {/* Items */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Barang Dibeli</p>
                  <div className="border border-slate-200 rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 text-xs text-slate-500">
                        <tr>
                          <th className="text-left py-2 px-3">Nama Barang</th>
                          <th className="text-left py-2 px-3">Kode</th>
                          <th className="text-right py-2 px-3">Harga</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detail.items || []).map((it, i) => (
                          <tr key={i} className="border-t border-slate-100">
                            <td className="py-2 px-3 font-medium text-slate-900">
                              {it.nama_barang}
                              {it.is_bonus && (
                                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-semibold align-middle">Bonus</span>
                              )}
                              {/* Diskon per barang (N2) — Harga baris tetap bruto;
                                  jumlahnya adalah baris Diskon di bawah tabel. */}
                              {diskonBaris(it) > 0 && (
                                <span className="block text-[11px] font-normal text-rose-700" data-testid={`sale-detail-item-diskon-${i}`}>
                                  Diskon −{idr(diskonBaris(it))}
                                </span>
                              )}
                            </td>
                            <td className="py-2 px-3"><span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-xs">{it.kode || "HP"}</span></td>
                            <td className="py-2 px-3 text-right font-mono">{idr(it.harga)}</td>
                          </tr>
                        ))}
                      </tbody>
                      {/* Subtotal − Diskon = Total (brief owner 22 Sep 2026).
                          Dulu "Total Harga" = `total_harga` bruto, sementara
                          diskonnya tercetak kecil di blok Pembayaran — owner
                          membacanya sebagai total yang belum dipotong. Kedua
                          angka turunan datang dari server; tanpa diskon tampilan
                          tetap satu baris seperti dulu. */}
                      <tfoot className="bg-slate-50">
                        {detail._diskon_nota > 0 && (
                          <>
                            <tr>
                              <td colSpan={2} className="py-1.5 px-3 text-right text-xs text-slate-500 font-semibold">Subtotal</td>
                              <td className="py-1.5 px-3 text-right font-mono" data-testid="sale-detail-subtotal">{idr(detail.total_harga)}</td>
                            </tr>
                            <tr className="text-rose-700">
                              <td colSpan={2} className="py-1.5 px-3 text-right text-xs font-semibold">Diskon</td>
                              <td className="py-1.5 px-3 text-right font-mono" data-testid="sale-detail-diskon">-{idr(detail._diskon_nota)}</td>
                            </tr>
                          </>
                        )}
                        {/* Nota ber-PPN: angka ini SEBELUM PPN, sementara
                            faktur mencetak Total = DPP+PPN — tanpa keterangan,
                            satu nota terbaca punya dua "Total". */}
                        <tr>
                          <td colSpan={2} className="py-2 px-3 text-right text-xs text-slate-500 font-semibold" data-testid="sale-detail-total-label">
                            {detail._diskon_nota > 0 ? "Total" : "Total Harga"}{detail.kena_ppn ? " (sebelum PPN)" : ""}
                          </td>
                          <td className="py-2 px-3 text-right font-mono font-bold" data-testid="sale-detail-total">{idr(detail._total_setelah_diskon)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Payment */}
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Pembayaran</p>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm">
                    {/* Nota BERTEMPO (brief owner 22 Sep 2026): dulu tampil
                        "Transfer BCA Rp 12.500.000" — fallback
                        `harga_terima || total_harga` yang terbaca "sudah dibayar
                        penuh" padahal uangnya menyusul lewat Piutang Usaha.
                        Yang ditampilkan kini hanya fakta tersimpan: uang yang
                        diterima saat nota dibuat (`total_payments`) dan status
                        piutangnya (`_piutang_nota`, dari dokumen Finance —
                        nota "BET" misalnya lunas lewat Finance, bukan di kasir). */}
                    {/* Nota BERTANDA (batch N3, D4/D5/D7): baris pembayaran dari
                        server (`_baris_pembayaran`) — metode + keterangan,
                        trade-in, DP, sisa tempo/kembalian. Nota lama tetap
                        memakai tampilan di cabang sesudahnya, persis seperti dulu. */}
                    {detail.bayar_per_baris ? (
                      <div className="space-y-1.5" data-testid="sale-detail-bayar-baris">
                        {detail._bertempo && (
                          <div className="flex justify-between items-center gap-2">
                            <span className="text-amber-800 font-medium inline-flex items-center gap-1.5">
                              <Clock className="w-4 h-4" /> Tempo {detail._tempo_label}
                            </span>
                            {detail.tanggal_jatuh_tempo && (
                              <span className="text-xs text-slate-500">Jatuh tempo {formatTanggal(detail.tanggal_jatuh_tempo)}</span>
                            )}
                          </div>
                        )}
                        <DaftarBarisPembayaran baris={detail._baris_pembayaran?.baris} testidPrefix="sale-detail-bayar"
                          kosong={<p className="text-xs text-slate-500">Tidak ada pembayaran di kasir.</p>} />
                        {detail._bertempo && detail._piutang_nota && (
                          <p className="text-xs text-slate-600" data-testid="sale-detail-bayar-piutang">
                            Piutang{detail._piutang_nota.code ? ` ${detail._piutang_nota.code}` : ""} · {STATUS_PIUTANG_LABEL[detail._piutang_nota.status] || detail._piutang_nota.status}
                            {detail._piutang_nota.sisa != null && detail._piutang_nota.status !== "dibatalkan"
                              ? ` · sisa sekarang ${idr(detail._piutang_nota.sisa)}` : ""}
                          </p>
                        )}
                        {/* D6: angka yang diketik kasir saat approve — sudah
                            dicocokkan server dengan uang yang seharusnya diterima. */}
                        {detail.total_pembayaran_kasir !== null && detail.total_pembayaran_kasir !== undefined && (
                          <p className="text-xs text-slate-500" data-testid="sale-detail-total-kasir">
                            Total Pembayaran dikonfirmasi kasir: <span className="font-mono font-semibold text-slate-800">{idr(detail.total_pembayaran_kasir)}</span>
                            {detail.total_pembayaran_kasir_info?.oleh_nama ? ` (${detail.total_pembayaran_kasir_info.oleh_nama})` : ""}
                          </p>
                        )}
                      </div>
                    ) : (<>
                    {detail._bertempo ? (
                      <div className="space-y-1" data-testid="sale-detail-tempo">
                        <div className="flex justify-between items-center gap-2">
                          <span className="text-amber-800 font-medium inline-flex items-center gap-1.5">
                            <Clock className="w-4 h-4" /> Tempo {detail._tempo_label}
                          </span>
                          {detail.tanggal_jatuh_tempo && (
                            <span className="text-xs text-slate-500">Jatuh tempo {formatTanggal(detail.tanggal_jatuh_tempo)}</span>
                          )}
                        </div>
                        {detail.payments && detail.payments.length > 1 && detail.payments.map((p, i) => (
                          <div key={i} className="flex justify-between">
                            <span className="text-slate-600">· {p.method}</span>
                            <span className="font-mono">{idr(p.amount)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between" data-testid="sale-detail-tempo-diterima">
                          <span className="text-slate-600">
                            Diterima saat nota dibuat
                            {(detail.total_payments || 0) > 0 && !(detail.payments && detail.payments.length > 1) && detail.metode_pembayaran
                              ? ` (${detail.metode_pembayaran})` : ""}
                          </span>
                          <span className="font-mono">{idr(detail.total_payments || 0)}</span>
                        </div>
                        {/* Kode & angka piutang hanya dikirim server untuk
                            pemegang finance_piutang.view; tanpa izin itu yang
                            datang cuma status kasar — kolom angkanya tidak
                            dirender, bukan diisi tebakan. */}
                        {detail._piutang_nota ? (
                          <div className="flex justify-between" data-testid="sale-detail-piutang-status">
                            <span className="text-slate-600">
                              Piutang{detail._piutang_nota.code ? ` ${detail._piutang_nota.code}` : ""} · {STATUS_PIUTANG_LABEL[detail._piutang_nota.status] || detail._piutang_nota.status}
                            </span>
                            {detail._piutang_nota.sisa != null && (
                              <span className="font-mono font-semibold">
                                {detail._piutang_nota.status === "dibatalkan" ? "—"
                                  : detail._piutang_nota.sisa > 0 ? `Sisa ${idr(detail._piutang_nota.sisa)}`
                                  : `Terbayar ${idr(detail._piutang_nota.terbayar)}`}
                              </span>
                            )}
                          </div>
                        ) : detail._tempo_lunas_di_tempat ? (
                          // Sah: nota bertempo yang dibayar penuh di tempat
                          // memang tidak melahirkan piutang (server yang
                          // memutuskan lewat sisa_tagihan_nota). Kalimatnya
                          // menyebut apa yang TERCATAT, bukan menjamin benar.
                          <p className="text-xs text-slate-500" data-testid="sale-detail-piutang-status">
                            Tidak ada piutang — nilai nota tercatat diterima penuh saat nota dibuat.
                          </p>
                        ) : (
                          <p className="text-xs text-slate-500" data-testid="sale-detail-piutang-status">
                            Belum ada dokumen piutang untuk nota ini.
                          </p>
                        )}
                      </div>
                    ) : detail.payments && detail.payments.length > 1 ? (
                      <div className="space-y-1">
                        {detail.payments.map((p, i) => (
                          <div key={i} className="flex justify-between">
                            <span className="text-slate-600">· {p.method}</span>
                            <span className="font-mono">{idr(p.amount)}</span>
                          </div>
                        ))}
                        <div className="border-t border-slate-200 pt-1 mt-1 flex justify-between font-semibold">
                          <span>Total Split</span>
                          <span className="font-mono">{idr(detail.total_payments)}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex justify-between">
                        <span className="text-slate-700 inline-flex items-center gap-1.5"><CreditCard className="w-4 h-4" />{detail.metode_pembayaran}</span>
                        <span className="font-mono font-semibold">{idr(detail.harga_terima || detail.total_harga)}</span>
                      </div>
                    )}
                    </>)}
                    {/* Diskon pindah ke tabel Barang Dibeli (Subtotal − Diskon =
                        Total, brief 22 Sep 2026) — sekali saja, di samping total
                        yang dikuranginya. Dulu dibaca dari `diskon_nominal`
                        mentah, sehingga diskon nota jalur Input Nota lama (kunci
                        `diskon`) tidak tampil sama sekali. */}
                    {(detail.biaya_admin_rp > 0 || detail.kena_ppn) && (
                      <div className="mt-2 pt-2 border-t border-slate-200 text-xs space-y-0.5">
                        {detail.biaya_admin_rp > 0 && (
                          <div className="flex justify-between text-emerald-700"><span>Biaya Admin</span><span className="font-mono">+{idr(detail.biaya_admin_rp)}</span></div>
                        )}
                        {/* PPN (keputusan owner 18 Agu 2026) — DPP & PPN
                            ditampilkan terpisah, angkanya dari server (bukan
                            pratinjau klien), termasuk tarif yang tersimpan di
                            dokumen (bisa beda dari tarif berlaku saat ini). */}
                        {detail.kena_ppn && (
                          <>
                            <div className="flex justify-between text-slate-600" data-testid="detail-dpp">
                              <span>DPP</span><span className="font-mono">{idr(detail.dpp)}</span>
                            </div>
                            <div className="flex justify-between text-emerald-700" data-testid="detail-ppn-nominal">
                              <span>PPN ({detail.ppn_persen ?? 11}%)</span><span className="font-mono">+{idr(detail.ppn_nominal)}</span>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                    {detail.trade_in && (
                      <div className="mt-2 flex items-center gap-2 text-xs text-amber-800">
                        <Repeat className="w-3.5 h-3.5" /> <b>Trade-in:</b> {detail.trade_in_note || "-"}
                      </div>
                    )}
                  </div>
                </div>

                {/* Meta */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <Field label="IMEI"><span className="font-mono">{detail.imei || "-"}</span></Field>
                  <Field label="Penjual">{detail.nama_penjual}</Field>
                  <Field label="Catatan"><span className="text-slate-600 italic">{detail.catatan || "-"}</span></Field>
                  <Field label="Input oleh"><span className="text-xs text-slate-600">{detail.created_by}</span></Field>
                </div>

                {/* Edit history */}
                {(detail.edit_count || 0) > 0 && (
                  <div className="bg-violet-50 border border-violet-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-violet-800 flex items-center gap-1.5 mb-1">
                      <History className="w-3.5 h-3.5" /> Riwayat Edit ({detail.edit_count}×)
                    </p>
                    <ul className="text-xs text-violet-900/80 space-y-0.5">
                      {(detail.edit_history || []).map((h, i) => (
                        <li key={i}>
                          · {formatTanggal(h.at, { denganJam: true })} — {h.by}
                          {/* Lengkapi No HP (I1) bukan edit nota biasa: No HP-nya
                              menentukan pemilik Kredit Retur, jadi alasannya ikut tampil. */}
                          {h.aksi === "lengkapi_no_hp" && (
                            <span data-testid={`sales-riwayat-lengkapi-hp-${i}`}>
                              {" "}— Lengkapi No HP{h.alasan ? `: ${h.alasan}` : ""}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-between items-center pt-2 border-t border-slate-100 flex-wrap gap-2">
                  <div className="text-xs text-slate-500">
                    {/* Nota bertempo TIDAK punya jendela 60 menit (F5) — menampilkan
                        hitung mundurnya membuat label berbohong: "Bisa diedit ~0
                        menit lagi" pada nota yang justru boleh diedit selamanya,
                        atau "Waktu edit sudah lewat" pada nota yang tombol Editnya
                        tetap ada. Ketiga keadaan dipisah eksplisit. */}
                    {detail._boleh_edit_tempo ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700"><Pencil className="w-3.5 h-3.5" /> Nota bertempo — bisa diedit SPV berwenang, tanpa batas waktu</span>
                    ) : detail._tempo_terkunci_pembayaran ? (
                      <span className="inline-flex items-center gap-1 text-amber-700"><Lock className="w-3.5 h-3.5" /> Piutangnya sudah dibayar — koreksi lewat Edit Pembayaran di Finance</span>
                    ) : detail._can_edit ? (
                      <span className="inline-flex items-center gap-1 text-emerald-700"><Clock className="w-3.5 h-3.5" /> Bisa diedit ~{minutesLeft(detail.created_at, detail._edit_window_minutes)} menit lagi</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-slate-500"><Lock className="w-3.5 h-3.5" /> Waktu edit sudah lewat ({detail._edit_window_minutes}m)</span>
                    )}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {/* Cetak faktur/struk (brief #17-19) — dua tombol pilih JENIS dokumen,
                        pemilihan TEMPLATE-nya (kalau ada lebih dari satu) terjadi di dalam
                        modal cetak (lihat bukaCetak()). Kedua tombol SELALU tampil — tidak
                        lagi hilang diam-diam kalau template belum dibuat. */}
                    <button data-testid="btn-cetak-faktur" onClick={() => bukaCetak("penjualan")}
                      className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5">
                      <Printer className="w-4 h-4" /> Cetak Faktur
                    </button>
                    <button data-testid="btn-cetak-struk" onClick={() => bukaCetak("struk")}
                      className="h-10 px-4 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 text-sm font-medium inline-flex items-center gap-1.5">
                      <Printer className="w-4 h-4" /> Cetak Struk
                    </button>
                    {can("sales", "create") && (
                      <button data-testid="btn-kirim-faktur-wa" onClick={kirimFakturWA}
                        className="h-10 px-4 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm font-medium inline-flex items-center gap-1.5">
                        <Send className="w-4 h-4" /> Kirim Faktur WA
                      </button>
                    )}
                    {/* CSAT: buat tautan penilaian lalu buka WhatsApp dengan teks siap kirim.
                        Idempoten di backend — klik dua kali menghasilkan tautan yang sama. */}
                    <button data-testid="sale-detail-csat"
                      onClick={async () => {
                        try {
                          const r = await api.post("/csat/generate", { sale_id: detail.id });
                          const link = r.data.wa_link;
                          if (link) { window.open(link, "_blank"); toast.success("Tautan CSAT siap — WhatsApp terbuka"); }
                          else toast.error("Tautan tidak terbentuk — cek no. HP pelanggan");
                        } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membuat tautan CSAT"); }
                      }}
                      className="h-10 px-4 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-sm font-medium inline-flex items-center gap-1.5">
                      <Send className="w-4 h-4" /> Kirim CSAT
                    </button>
                    {/* Ajukan retur (brief bos) — hanya dokumen, jurnal/stok bergerak
                        saat disetujui SPV/admin di halaman /retur. */}
                    {can("retur", "create") && (
                      <button onClick={() => openReturModal(detail)} data-testid={`btn-retur-${detail.id}`}
                        className="h-10 px-4 rounded-lg border border-rose-300 text-rose-700 hover:bg-rose-50 text-sm font-medium inline-flex items-center gap-1.5">
                        <Undo2 className="w-4 h-4" /> Retur
                      </button>
                    )}
                    {/* Retur SUDAH DISETUJUI mengunci Hapus & Edit (owner, 16 Agu 2026) — returnya
                        sudah membalikkan uang lewat jurnalnya sendiri, jadi menghapus/mengubah
                        penjualannya sekarang akan membalik kas & pendapatan untuk kedua kalinya.
                        Ditampilkan sebagai penjelasan, BUKAN tombol yang ujungnya 409 (aturan #3
                        CLAUDE.md) — backend (`_retur_penjualan_disetujui` di routers/sales.py)
                        tetap menolak permintaan ini walau tombolnya sengaja dipaksa tampil. */}
                    {detail.has_retur_disetujui ? (
                      <span data-testid="sale-detail-locked-retur"
                        title={`Retur ${detail.retur_disetujui_code || ""} sudah disetujui — koreksi lewat menu Retur Penjualan`}
                        className="h-10 px-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 text-sm font-medium inline-flex items-center gap-1.5">
                        <Lock className="w-4 h-4" />
                        Terkunci — retur {detail.retur_disetujui_code || ""} sudah disetujui
                      </span>
                    ) : (
                      <>
                        {canEdit && (
                          <button onClick={() => del(detail.id)} data-testid="sale-detail-delete"
                            className="h-10 px-4 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 text-sm font-medium inline-flex items-center gap-1.5">
                            <Trash2 className="w-4 h-4" /> Hapus
                          </button>
                        )}
                        {/* F5: nota bertempo yang piutangnya SUDAH menerima uang tidak
                            bisa diedit — dijelaskan, bukan dibiarkan jadi tombol yang
                            ujungnya 409 (aturan 3 CLAUDE.md). Koreksinya lewat Edit
                            Pembayaran di menu Piutang Usaha (F8). */}
                        {canEdit && detail._tempo_terkunci_pembayaran && (
                          <span data-testid="sale-detail-locked-tempo"
                            title="Perbaiki lewat Edit Pembayaran di menu Finance → Piutang Usaha"
                            className="h-10 px-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 text-sm font-medium inline-flex items-center gap-1.5">
                            <Lock className="w-4 h-4" />
                            Terkunci — piutangnya sudah dibayar sebagian
                          </span>
                        )}
                        {/* Retur yang MASIH MENUNGGU approval mengunci Edit (tinjauan gelombang 3,
                            `_retur_penjualan_menunggu` di routers/sales.py) — diedit sekarang, retur itu
                            dinilai atas isi nota yang sudah berbeda. Hapus tetap boleh. */}
                        {canEdit && detail._can_edit && detail.has_retur_menunggu && (
                          <span data-testid="sale-detail-locked-retur-menunggu"
                            title="Setujui atau tolak retur itu dulu di menu Retur Penjualan"
                            className="h-10 px-4 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 text-sm font-medium inline-flex items-center gap-1.5">
                            <Lock className="w-4 h-4" />
                            Edit terkunci — retur {detail.retur_menunggu_code || ""} menunggu approval
                          </span>
                        )}
                        {canEdit && detail._can_edit && !detail.has_retur_menunggu && (
                          <button onClick={() => openEdit(detail)} data-testid="sale-detail-edit"
                            className="h-10 px-4 rounded-lg bg-[#25D366] hover:bg-[#128C7E] text-white text-sm font-medium inline-flex items-center gap-1.5">
                            <Pencil className="w-4 h-4" /> Edit
                            {detail._boleh_edit_tempo && " Nota Bertempo"}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CREATE / EDIT MODAL */}
      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={saleGuard.requestClose}>
          <div ref={saleGuard.contentRef} className="bg-white rounded-2xl w-full max-w-2xl my-8" onClick={e => e.stopPropagation()}>
            <div className="sticky top-0 bg-[#25D366] text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                <h3 className="font-display font-semibold">{modalMode === "edit" ? "Edit Penjualan" : "Input Penjualan"}</h3>
              </div>
              <button onClick={saleGuard.requestClose} className="p-2 hover:bg-white/20 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {/* N1 (brief 22 Sep 2026): blok Jenis Pelanggan/Tempo (F5) di ATAS
                  nama pelanggan — jenis menentukan apakah nama DIPILIH dari Data
                  Pelanggan, jadi diputuskan lebih dulu (sama dengan Input Nota). */}
              {/* F5 (brief owner 12 Sep 2026) — Jenis Pelanggan & Metode
                  Pembayaran/Tempo pada nota B2B/USER yang sudah melahirkan
                  piutang. Blok ini SENGAJA hanya muncul saat server bilang
                  boleh (`_boleh_edit_tempo`): menampilkannya pada nota tunai
                  biasa berarti kasir pemegang `sales.update` bisa menerbitkan
                  piutang lewat pintu belakang, tanpa satu approval pun. */}
              {modalMode === "edit" && form._boleh_edit_tempo && (
                <div className="rounded-xl border border-sky-200 bg-sky-50/60 p-3 space-y-3"
                     data-testid="sale-blok-tempo">
                  <p className="text-xs font-semibold uppercase tracking-wider text-sky-800">
                    Nota Bertempo — Piutang
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <F l="Jenis Pelanggan">
                      <select value={form.jenis_pelanggan || "umum"} data-testid="sale-jenis-pelanggan"
                        onChange={e => setForm(s => ({ ...s, jenis_pelanggan: e.target.value }))}
                        className="h-9 w-full px-3 rounded border border-slate-200 text-sm bg-white">
                        <option value="umum">Umum</option>
                        <option value="b2b">B2B</option>
                        <option value="user">USER</option>
                      </select>
                    </F>
                    <F l="Metode Pembayaran / Tempo">
                      <select value={form.tempo_pembayaran || ""} data-testid="sale-tempo"
                        onChange={e => gantiTempo(e.target.value)}
                        className="h-9 w-full px-3 rounded border border-slate-200 text-sm bg-white">
                        {[["", "Tanpa tempo (bayar di tempat)"], ["net_3", "Net 3 hari"],
                          ["net_7", "Net 7 hari"], ["net_10", "Net 10 hari"], ["net_30", "Net 30 hari"],
                          ["net_90", "Net 90 hari"], ["deposit", "Deposit"]].map(([v, label]) => (
                          <option key={v || "tunai"} value={v} disabled={!!v && t2TempoMati}
                            data-testid={`sale-tempo-opsi-${v || "tunai"}`}>{label}</option>
                        ))}
                      </select>
                    </F>
                    <F l="PIC Persetujuan Piutang">
                      <input value={form.pic_approval_piutang || ""} data-testid="sale-pic-piutang"
                        onChange={e => setForm(s => ({ ...s, pic_approval_piutang: e.target.value }))}
                        placeholder="wajib untuk jenis USER"
                        className="h-9 w-full px-3 rounded border border-slate-200 text-sm" />
                    </F>
                  </div>
                  {t2TempoMati && (
                    <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                      data-testid="sale-t2-alasan-tempo">
                      {form._tempo_tradein_alasan}
                    </p>
                  )}
                  <p className="text-[11px] text-sky-900">
                    Tanggal jatuh tempo dihitung ulang server dari tanggal nota + temponya, dan
                    dokumen piutangnya ikut disesuaikan begitu perubahan ini disimpan.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <F l="Tanggal"><DatePicker value={form.tanggal} onChange={v => setForm(s => ({ ...s, tanggal: v }))} data-testid="sale-tanggal" /></F>
                <F l="Waktu"><input type="time" value={form.waktu} onChange={e => setForm(s => ({ ...s, waktu: e.target.value }))} className={inp} data-testid="sale-waktu" /></F>
                <F l="Branch *"><select value={form.branch} onChange={e => setForm(s => ({ ...s, branch: e.target.value }))}
                  data-testid="sale-branch" className={inp + " bg-white" + (galatCabang ? " border-rose-400" : "")}>
                  {BRANCHES.map(b => <option key={b} value={b}>{b || "— pilih —"}</option>)}
                  {/* Ejaan cabang lama yang tidak ada di daftar tetap tampil & terkirim
                      apa adanya — membuka lalu menyimpan tidak mengganti cabangnya. */}
                  {form.branch && !BRANCHES.includes(form.branch) && (
                    <option value={form.branch}>{form.branch} (tidak terdaftar)</option>
                  )}
                </select>
                {galatCabang && <span className="text-[11px] text-rose-700" data-testid="sale-branch-galat">{galatCabang}</span>}
                </F>
                {/* <div>, bukan <F> (<label>): kolom ini memuat tombol Ganti &
                    "+ Pelanggan baru" — di dalam <label>, klik pada teks mana pun
                    diteruskan browser ke tombol pertama. */}
                <div className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-slate-600">Nama Pelanggan *</span>
                  <PilihPelangganNota
                    jenis={form.jenis_pelanggan}
                    nama={form.nama_pelanggan}
                    onNama={v => setForm(s => ({
                      ...s, nama_pelanggan: v,
                      _kartu: s._kartu && kunciNama(s._kartu.nama) === kunciNama(v) ? s._kartu : null,
                    }))}
                    onPilih={pilihKartu}
                    kartu={form._kartu}
                    onLepas={() => setForm(s => ({ ...s, _kartu: null, nama_pelanggan: "" }))}
                    noHp={form.no_hp}
                    aturan={aturanIdentitas}
                    perluKartu={perluKartu}
                    testid="sale-nama-pelanggan"
                    className={inp}
                  />
                </div>
                <F l="No HP *"><input value={form.no_hp} onChange={e => setForm(s => ({ ...s, no_hp: e.target.value }))}
                  data-testid="sale-no-hp" inputMode="tel" placeholder="0812 3456 7890"
                  className={inp + (galatHpTampil ? " border-rose-400" : "")} />
                {galatHpTampil && <span className="text-[11px] text-rose-700" data-testid="sale-no-hp-galat">{galatHpTampil}</span>}
                {identitasAwal && !identitasAwal.no_hp && !hpBerubah && (
                  <span className="text-[11px] text-slate-500" data-testid="sale-no-hp-lama">
                    Transaksi lama tanpa No HP — boleh disimpan apa adanya; kalau diisi, harus nomor yang sah.
                  </span>
                )}
                </F>
                <F l="IMEI"><input value={form.imei} onChange={e => setForm(s => ({ ...s, imei: e.target.value }))} className={inp} placeholder="opsional" /></F>
              </div>

              {/* Sumber Info / atribusi marketing (brief bos 11 Agu 2026) —
                  lihat components/SourceInfoField.jsx untuk alasan lengkap. */}
              <SourceInfoField
                sourceInfo={form.source_info}
                sourceRefId={form.source_ref_id}
                onChange={(v, refId) => setForm(s => ({ ...s, source_info: v, source_ref_id: refId }))}
                cfg={sourceInfoCfg}
                testidPrefix="sale-source-info"
              />

              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Barang Dibeli</p>
                  <button onClick={addItem} data-testid="btn-add-item"
                    className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800">
                    <Plus className="w-3.5 h-3.5" /> Tambah Barang
                  </button>
                </div>
                <div className="space-y-2">
                  {form.items.map((it, i) => {
                    // Paket/bundling (poin 8, brief bos 23 Agu 2026) — baris ini
                    // "paket" kalau SKU-nya adalah sku_bundle resep AKTIF (deteksi
                    // lewat KEBERADAAN resep, sama seperti `_tolak_stok_kosong` di
                    // backend — bukan lewat `it.komponen` terisi/tidak).
                    const resepBaris = it.sku ? resepBundlingMap[it.sku.trim().toUpperCase()] : null;
                    return (
                    <div key={i} className="grid grid-cols-[1fr,120px,150px,auto] gap-2 items-start border border-slate-200 rounded-lg p-3">
                      <div>
                        <ItemNamaField index={i} item={it} bolehLookup={bolehLookupProduk}
                          resepBundlingMap={resepBundlingMap}
                          onPatch={(patch) => patchItem(i, patch)} />
                        {/* IMEI baris ini HANYA muncul untuk barang yang sudah tertaut SKU
                            (brief bos poin 4) — barang ketik bebas tanpa SKU tidak pernah
                            dipaksa mengisi IMEI, lihat docstring ItemImeiField. Baris PAKET
                            (poin 8) tidak pernah punya unit fisik untuk SKU paketnya sendiri
                            (lihat docstring bundling.py) — IMEI-nya per KOMPONEN, dipilih
                            lewat BundleKomponenPicker di bawah, bukan field ini. */}
                        {bolehLookupUnit && it.sku && !resepBaris && (
                          <ItemImeiField index={i} item={it} bolehLookup={bolehLookupUnit}
                            imeiTerpakaiLain={new Set(form.items
                              .filter((_, j) => j !== i)
                              .map(x => (x.imei || "").trim())
                              .filter(Boolean))}
                            modeEdit={modalMode === "edit"}
                            onPatch={(patch) => patchItem(i, patch)} />
                        )}
                        {(resepBaris || (Array.isArray(it.komponen) && it.komponen.length > 0)) && (
                          <BundleKomponenPicker index={i} resep={resepBaris} komponen={it.komponen}
                            onChange={(next) => patchItem(i, { komponen: next })}
                            bolehLookupUnit={bolehLookupUnit}
                            imeiTerpakaiLain={kumpulkanSemuaImeiForm(form.items)}
                            produkInfoCache={produkInfoCache} ensureProdukInfo={ensureProdukInfo} />
                        )}
                      </div>
                      <div className="space-y-1">
                        <input type="number" placeholder="Harga" value={it.harga || ""}
                          onChange={e => updItem(i, "harga", e.target.value)}
                          data-testid={`sale-item-${i}-harga`}
                          className="h-9 w-full px-3 rounded border border-slate-200 text-sm text-right font-mono" />
                        {/* Diskon per barang (brief owner 22 Sep 2026, D3) — Rupiah
                            untuk SELURUH baris; harga tetap bruto, potongannya
                            dijurnal terpisah ke 4190. Baris bonus tidak berdiskon. */}
                        {!it.is_bonus && (
                          <InputRupiah value={it.diskon_rp || 0} onChange={v => updItem(i, "diskon_rp", v)}
                            placeholder="Diskon" testid={`sale-item-${i}-diskon`}
                            className={`h-8 w-full px-3 rounded border text-xs text-right font-mono ${
                              diskonBaris(it) > 0 ? "border-rose-300 text-rose-700" : "border-slate-200"}`} />
                        )}
                        {/* Backend menolak harga Rp0 kecuali ditandai bonus — checkbox ini
                            BUKAN cara "mengakali" harga, hanya untuk barang yang memang
                            diberikan gratis (bonus/hadiah/garansi pembelian). */}
                        <label className="flex items-center gap-1.5 text-[10px] text-slate-500 cursor-pointer"
                          title="Centang kalau barang ini memang diberikan GRATIS (bonus/hadiah/garansi) — bukan cara mengosongkan harga jual.">
                          <input type="checkbox" checked={!!it.is_bonus}
                            onChange={e => updItem(i, "is_bonus", e.target.checked)}
                            data-testid={`sale-item-${i}-bonus`} className="w-3 h-3 accent-emerald-600" />
                          Bonus/Gratis
                        </label>
                      </div>
                      {/* Baris bertaut SKU: kategori diambil APA ADANYA dari master
                          produk (lihat ItemNamaField.pilih()) — dikunci, bukan
                          dropdown, supaya kasir tidak bisa diam-diam menimpanya
                          dengan kategori yang salah (akun neraca ikut salah kalau
                          itu terjadi, lihat memory/MIGRASI-KATEGORI-23AGU.md).
                          Lepas SKU dulu (tombol × di badge SKU) kalau memang perlu
                          diketik bebas. Baris TANPA SKU tetap dropdown biasa, karena
                          tidak ada sumber kebenaran lain selain pilihan kasir. */}
                      {it.sku ? (
                        <div data-testid={`item-kategori-${i}`}
                          title="Kategori mengikuti master produk — lepas tautan SKU untuk mengubah manual"
                          className="h-9 px-2 rounded border border-slate-200 bg-slate-50 text-xs text-slate-600 flex items-center">
                          {labelKategori(it.kategori)}
                        </div>
                      ) : (
                        <select value={normalKategori(it.kategori) || KATEGORI_DEFAULT} onChange={e => updItem(i, "kategori", e.target.value)}
                          data-testid={`item-kategori-${i}`}
                          className="h-9 px-2 rounded border border-slate-200 text-xs bg-white">
                          {KATEGORI_ITEM.map(k => <option key={k.v} value={k.v}>{k.l}</option>)}
                        </select>
                      )}
                      {form.items.length > 1 && (
                        <button onClick={() => rmItem(i)}
                          className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                    );
                  })}
                </div>
                <div className="mt-2 flex justify-end text-sm">
                  <span className="text-slate-500 mr-2">Subtotal barang:</span>
                  <span className="font-mono font-bold text-slate-900">{idr(totalItems)}</span>
                </div>
              </div>

              {/* Ringkasan uang + PPN. Diskon per barang ada di baris barang;
                  diskon nota & biaya admin hanya sebagai sisa transaksi lama. */}
              <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Ringkasan &amp; PPN</p>
                  {/* PPN (keputusan owner 18 Agu 2026): CV ZOA berstatus PKP,
                      tarif 11%, harga barang di atas BELUM termasuk PPN. */}
                  <label className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={kenaPpn}
                      onChange={e => setForm(s => ({ ...s, kena_ppn: e.target.checked }))}
                      data-testid="sale-kena-ppn" className="w-3.5 h-3.5 accent-emerald-600" />
                    Kena PPN {PPN_PERSEN_PREVIEW}%
                  </label>
                </div>
                {/* Brief owner 22 Sep 2026 (D2): kolom Diskon nota & Biaya Admin
                    DIHAPUS — diskon kini per barang (kolom Diskon di tiap baris
                    di atas). Transaksi LAMA yang sudah memakainya tetap dihitung
                    & dijurnal; di sini tampil baca-saja dan hanya bisa dihapus. */}
                {form._diskon_lama?.ada && (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs" data-testid="sale-diskon-lama">
                    {form._diskon_lama.dihapus ? (
                      <p className="text-amber-900">
                        Diskon nota / biaya admin lama akan <b>dihapus</b> saat disimpan.{" "}
                        <button type="button" className="underline" data-testid="btn-batal-hapus-diskon-lama"
                          onClick={() => setForm(s => ({ ...s, _diskon_lama: { ...s._diskon_lama, dihapus: false } }))}>
                          Batalkan
                        </button>
                      </p>
                    ) : (
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-amber-900">
                          Transaksi lama:
                          {form._diskon_lama.diskon > 0 && (
                            <> diskon nota <b className="font-mono">{idr(form._diskon_lama.diskon)}</b></>
                          )}
                          {form._diskon_lama.mode && form._diskon_lama.nilai > 0 && (
                            <> · biaya admin <b className="font-mono">
                              {form._diskon_lama.mode === "persen" ? `${form._diskon_lama.nilai}%` : idr(form._diskon_lama.nilai)}
                            </b></>
                          )}
                          {" "}— tetap dihitung. Kolomnya sudah tidak ada di form baru.
                        </p>
                        <button type="button" data-testid="btn-hapus-diskon-lama"
                          onClick={() => setForm(s => ({ ...s, _diskon_lama: { ...s._diskon_lama, dihapus: true } }))}
                          className="h-7 px-2 rounded bg-white border border-amber-300 text-amber-900 hover:bg-amber-100">
                          Hapus diskon lama
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {diskonBarisForm > 0 && (
                  <F l="PIC Approval Diskon *">
                    <input value={form.diskon_pic_approval || ""} data-testid="sale-diskon-pic"
                      onChange={e => setForm(s => ({ ...s, diskon_pic_approval: e.target.value }))}
                      placeholder="Siapa yang menyetujui diskon barang" className={inp} />
                  </F>
                )}
                {/* Ringkasan hidup — SATU angka yang menjadi acuan validasi split payment. */}
                <div className="bg-white border border-slate-200 rounded-lg p-3 text-xs space-y-1">
                  <div className="flex justify-between text-slate-600">
                    <span>Subtotal barang</span><span className="font-mono">{idr(totalItems)}</span>
                  </div>
                  {diskonNominal > 0 && (
                    <div className="flex justify-between text-rose-700">
                      <span>− Diskon</span><span className="font-mono">-{idr(diskonNominal)}</span>
                    </div>
                  )}
                  {biayaAdminRp > 0 && (
                    <div className="flex justify-between text-emerald-700">
                      <span>+ Biaya Admin{biayaAdminMode === "persen" ? ` (${biayaAdminNilai || 0}%)` : ""}</span>
                      <span className="font-mono">+{idr(biayaAdminRp)}</span>
                    </div>
                  )}
                  {kenaPpn && (
                    <div className="flex justify-between text-emerald-700" data-testid="sale-ringkasan-ppn">
                      <span>+ PPN ({PPN_PERSEN_PREVIEW}%)</span>
                      <span className="font-mono">+{idr(ppnPreview)}</span>
                    </div>
                  )}
                  {tradeInTotal > 0 && (
                    <div className="flex justify-between text-violet-700">
                      <span>− Trade-in</span><span className="font-mono">-{idr(tradeInTotal)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-slate-900 pt-1 border-t border-slate-100">
                    <span>Total harus dibayar</span>
                    <span className="font-mono" data-testid="sale-total-harus-dibayar">{idr(totalHarusDibayar)}</span>
                  </div>
                  {/* DP hanya pada nota Input Nota yang dibuka untuk diedit — tanpa
                      baris ini acuan split (uang di kasir) terlihat tidak cocok
                      dengan "Total harus dibayar" di atasnya. */}
                  {!notaBertempo && rincianLayar.dp > 0 && (
                    <>
                      <div className="flex justify-between text-sky-700">
                        <span>− DP (sudah diterima)</span><span className="font-mono">-{idr(rincianLayar.dp)}</span>
                      </div>
                      <div className="flex justify-between font-semibold text-slate-900">
                        <span>Dibayar di kasir</span>
                        <span className="font-mono" data-testid="sale-dibayar-di-kasir">{idr(rincianLayar.diTempat)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* Blok pembayaran (batch N3): form BERTANDA (transaksi baru & yang
                  lahir dari layar N3) memakai baris metode + keterangan, trade-in DI
                  BAWAHNYA (D5). Transaksi LAMA tetap form lama persis. */}
              {form._bayar_per_baris ? (
                <>
                  <BlokPembayaran rows={form.payments} groups={paymentGroups}
                    onChange={rows => setForm(s => ({ ...s, payments: rows }))}
                    rincian={rincianLayar} tempo={notaBertempo} testidPrefix="sale-bayar"
                    sembunyikanKreditRetur={modalMode === "edit"}>
                    <div className="grid grid-cols-1 gap-2">
                        {renderTradeIn()}
                    </div>
                    {downgradeBayar && (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2"
                        data-testid="sale-refund-blok">
                        <p className="sm:col-span-3 text-[11px] text-amber-900">
                          {form._rekening_terkunci
                            ? "Rekening pengembalian sudah tercatat. Kalau salah, SPV/CEO Finance mencatat rekening yang benar saat membayar (Finance › Utang › Pengembalian ke Pelanggan)."
                            : "Transaksi DOWNGRADE — selisihnya ditransfer Finance. Isi rekening tujuan pengembalian dana."}
                        </p>
                        {[["refund_nama_bank", "Nama Bank *", "sale-refund-bank"],
                          ["refund_no_rekening", "Nomor Rekening *", "sale-refund-rek"],
                          ["refund_atas_nama", "Atas Nama *", "sale-refund-nama"]].map(([k, label, tid]) => (
                          <input key={k} value={form[k] || ""} placeholder={label} data-testid={tid}
                            disabled={form._rekening_terkunci}
                            onChange={e => setForm(s => ({ ...s, [k]: e.target.value }))}
                            className="h-9 px-3 rounded border border-slate-200 text-sm bg-white disabled:bg-slate-100" />
                        ))}
                      </div>
                    )}
                  </BlokPembayaran>
                  {/* Petunjuk Kredit Retur (I1) tepat di bawah blok pembayaran —
                      tempat baris "Kredit Retur" dipilih — bukan di kolom
                      identitas. Transaksi baru selalu bertanda, jadi hanya di sini. */}
                  {modalMode === "create" && <PetunjukKreditRetur info={kreditInfo} />}
                  {galatBayar && (
                    <p className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2"
                       data-testid="sale-bayar-galat">{galatBayar}</p>
                  )}
                  {lunasTempo && (
                    <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 space-y-1.5"
                      data-testid="sale-bayar-lunas-tempo">
                      <p className="text-[11px] text-amber-900">
                        Nota ini tadinya bertempo ({form._tempo_awal}). Disimpan tanpa tempo = piutangnya
                        DIBATALKAN dan uang di atas dicatat SUDAH diterima di kasir. Kalau pelunasannya
                        menyusul, biarkan tetap bertempo dan catat pembayarannya lewat menu Piutang.
                      </p>
                      <label className="inline-flex items-center gap-2 text-xs font-medium text-amber-900 cursor-pointer">
                        <input type="checkbox" checked={lunasDinyatakan} data-testid="sale-bayar-lunas-tempo-cek"
                          onChange={e => setForm(s => ({ ...s,
                            _pelunasan_tempo_nominal: e.target.checked ? uangLunasTempo : null }))} />
                        Pelanggan sudah melunasi {idr(uangLunasTempo)} di kasir
                      </label>
                    </div>
                  )}
                </>
              ) : (
                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
                      <CreditCard className="w-3.5 h-3.5" /> Metode Pembayaran
                    </p>
                    <label className="inline-flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={form.is_split}
                        onChange={e => setForm(s => ({
                          ...s,
                          is_split: e.target.checked,
                          payments: e.target.checked && (!s.payments || s.payments.length === 0)
                            ? [{ method: s.metode_pembayaran || "Cash", amount: acuanSplit }]
                            : s.payments,
                        }))}
                        data-testid="sale-split-toggle" />
                      <Split className="w-3.5 h-3.5" /> Split Payment
                    </label>
                  </div>

                  {!form.is_split ? (
                    <PaymentMethodSelect
                      value={form.metode_pembayaran}
                      onChange={v => setForm(s => ({ ...s, metode_pembayaran: v }))}
                      groups={paymentGroups}
                      /* Kredit Retur tidak ditawarkan saat edit (server 409) dan
                         untuk nota bertempo sebagai metode tunggal (server 400). */
                      sembunyikan={(modalMode === "edit" || notaBertempo) ? adalahKreditRetur : undefined}
                      className={inp + " bg-white"} testid="sale-metode" />
                  ) : (
                    <div className="space-y-2">
                      {(form.payments || []).map((p, i) => (
                        <div key={i} className="grid grid-cols-[1fr,120px,auto] sm:grid-cols-[1fr,180px,auto] gap-2 items-center">
                          <PaymentMethodSelect
                            value={p.method}
                            onChange={v => updPay(i, "method", v)}
                            groups={paymentGroups}
                            testid={`sale-pay-${i}-method`}
                            sembunyikan={modalMode === "edit" ? adalahKreditRetur : undefined}
                            className="h-9 px-3 rounded border border-slate-200 text-sm bg-white" />
                          <input type="number" placeholder="Nominal" value={p.amount || ""}
                            onChange={e => updPay(i, "amount", e.target.value)}
                            data-testid={`sale-pay-${i}-amount`}
                            className="h-9 px-3 rounded border border-slate-200 text-sm text-right font-mono" />
                          {(form.payments || []).length > 1 && (
                            <button onClick={() => rmPay(i)}
                              className="p-1.5 hover:bg-rose-50 rounded text-slate-400 hover:text-rose-600">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                      <div className="flex items-center justify-between pt-1">
                        <button onClick={addPay} data-testid="btn-add-pay"
                          className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 hover:text-emerald-800">
                          <Plus className="w-3.5 h-3.5" /> Tambah Metode
                        </button>
                        <div className="text-xs text-right">
                          <p className="text-slate-500">Total split: <span className="font-mono font-semibold text-slate-900">{idr(totalSplit)}</span></p>
                          <p className={Math.abs(splitBalance) < 0.5 ? "text-emerald-700" : "text-amber-700"}>
                            {Math.abs(splitBalance) < 0.5 ? "✓ Sesuai yang harus dibayar" : `Selisih ${idr(splitBalance)} dari ${idr(acuanSplit)}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {!form._bayar_per_baris && (
                  <>
                    <F l={notaBertempo ? "Uang Muka Diterima (Harga Terima)" : "Harga Terima"}>
                      {/* N0: angka KONFIRMASI (uang yang diterima di kasir). Kosong =
                          server menghitung dari rincian; placeholder = angka itu.
                          Nota bertempo: uang muka yang BENAR-BENAR sudah diterima. */}
                      <input type="number" value={form.harga_terima || ""}
                        onChange={e => setForm(s => ({ ...s, harga_terima: parseFloat(e.target.value) || 0,
                                                       _ht_disentuh: true }))}
                        className={inp + " text-right font-mono"}
                        placeholder={notaBertempo ? "0" : String(Math.max(0, Math.round(rincianLayar.diTempat)))}
                        data-testid="sale-harga-terima" />
                      {/* Bertempo -> tunai MEMBATALKAN piutang dan mendebit kas; server
                          hanya menerimanya kalau pelunasan dinyatakan eksplisit
                          (update_sale). Dijelaskan di sini supaya SPV tidak menabrak 400. */}
                      {modalMode === "edit" && (form._tempo_awal || "").trim() && !notaBertempo && (
                        <p className="text-[11px] mt-0.5 text-amber-700" data-testid="sale-petunjuk-lunas">
                          Nota ini tadinya bertempo — isi uang yang BENAR-BENAR sudah diterima
                          ({idr(Math.max(0, rincianLayar.diTempat))}) untuk menyatakan lunas di tempat.
                          Kalau pelunasannya menyusul, biarkan tetap bertempo.
                        </p>
                      )}
                    </F>
                    {renderTradeIn()}
                  </>
                )}
                <F l="Nama Penjual *">
                  <select value={form.nama_penjual}
                    onChange={e => setForm(s => ({ ...s, nama_penjual: e.target.value }))}
                    className={inp + " bg-white"} data-testid="sale-penjual">
                    <option value="">— pilih karyawan —</option>
                    {branchEmployees.map(e => (
                      <option key={e.id} value={e.nama}>
                        {e.nama}{e.branch ? ` · ${e.branch}` : ""}
                      </option>
                    ))}
                  </select>
                </F>
                <F l="Catatan">
                  <input value={form.catatan} onChange={e => setForm(s => ({ ...s, catatan: e.target.value }))}
                    className={inp} placeholder="opsional" />
                </F>
              </div>

              {/* Paket/bundling (poin 8-9, brief bos 23 Agu 2026) — alasan tombol
                  Simpan tertahan, aturan #3 CLAUDE.md: kasir harus tahu KENAPA
                  sebelum menekan, bukan menabrak toast 400 sesudahnya. */}
              {bundleAlasanTertahan && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                  data-testid="sale-bundle-alasan-tertahan">
                  {bundleAlasanTertahan}
                </p>
              )}
              {t2Ditahan && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                  data-testid="sale-t2-alasan-simpan">
                  {form._tempo_tradein_alasan}
                </p>
              )}
              <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                <button onClick={saleGuard.requestClose}
                  className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Batal</button>
                <button onClick={save} data-testid="btn-save-sale" disabled={saving || !!bundleAlasanTertahan || t2Ditahan}
                  title={bundleAlasanTertahan || (t2Ditahan ? form._tempo_tradein_alasan : undefined)}
                  className="h-10 px-5 rounded-lg bg-[#25D366] hover:bg-[#128C7E] disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-2">
                  {modalMode === "edit" ? <Pencil className="w-4 h-4" /> : <Send className="w-4 h-4" />}
                  {saving ? "Menyimpan..." : (modalMode === "edit" ? "Simpan Perubahan" : "Kirim")}
                </button>
              </div>
            </div>
            {saleGuard.confirming && (
              <UnsavedGuardBar onStay={saleGuard.cancelClose} onDiscard={saleGuard.confirmDiscard} testid="sale-modal-guard-bar" />
            )}
          </div>
        </div>
      )}

      {/* DRAFT WA AI MODAL */}
      {draftModal && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setDraftModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}
            data-testid="draft-wa-modal">
            <div className="bg-gradient-to-r from-emerald-600 to-emerald-500 text-white px-5 py-3.5 flex items-center justify-between rounded-t-2xl">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                <div>
                  <h3 className="font-display font-semibold text-sm">Draft Balasan WA — AI</h3>
                  <p className="text-[11px] text-emerald-50/90">Untuk: {draftModal.nama_pelanggan || "Pelanggan"} {draftModal.no_hp && `· ${draftModal.no_hp}`}</p>
                </div>
              </div>
              <button onClick={() => setDraftModal(null)}
                className="p-1.5 hover:bg-white/20 rounded-lg" data-testid="draft-wa-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              {draftModal.loading ? (
                <div className="min-h-[120px] flex flex-col items-center justify-center gap-2 text-sm text-slate-500">
                  <Sparkles className="w-6 h-6 text-emerald-500 animate-pulse" />
                  <span>GPT-5.2 sedang menyusun pesan...</span>
                </div>
              ) : (
                <>
                  <textarea
                    value={draftModal.message}
                    onChange={e => setDraftModal(m => ({ ...m, message: e.target.value }))}
                    rows={6}
                    data-testid="draft-wa-textarea"
                    className="w-full border border-slate-200 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-emerald-500/40 whitespace-pre-wrap"
                  />
                  <p className="text-[11px] text-slate-500">
                    <Info className="w-3 h-3 inline mb-0.5 mr-1" />
                    Tone: kasual chat teman. Bebas diedit sebelum dikirim.
                  </p>
                </>
              )}
            </div>
            <div className="border-t border-slate-100 px-5 py-3 flex flex-wrap gap-2 justify-between bg-slate-50 rounded-b-2xl">
              <button onClick={regenDraft} disabled={draftModal.loading} data-testid="draft-wa-regen"
                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 text-xs font-medium disabled:opacity-50">
                <Sparkles className="w-3.5 h-3.5" /> Susun Ulang
              </button>
              <div className="flex gap-2">
                <button onClick={copyDraft} disabled={draftModal.loading || !draftModal.message} data-testid="draft-wa-copy"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 text-xs font-medium disabled:opacity-50">
                  <Copy className="w-3.5 h-3.5" /> Salin
                </button>
                {draftModal.no_hp ? (
                  <a
                    href={`https://wa.me/${(draftModal.no_hp.replace(/\D/g, "").startsWith("0") ? "62" + draftModal.no_hp.replace(/\D/g, "").slice(1) : draftModal.no_hp.replace(/\D/g, ""))}?text=${encodeURIComponent(draftModal.message || "")}`}
                    target="_blank" rel="noopener noreferrer"
                    data-testid="draft-wa-send"
                    className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[#25D366] hover:bg-[#128C7E] text-white text-xs font-medium ${draftModal.loading ? "pointer-events-none opacity-50" : ""}`}>
                    <Send className="w-3.5 h-3.5" /> Buka WhatsApp
                  </a>
                ) : (
                  <span className="text-[11px] text-slate-400 self-center italic">No HP kosong — hanya bisa salin</span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CETAK FAKTUR / STRUK MODAL — alur: pilih jenis (tombol Cetak Faktur/
          Cetak Struk) sudah menentukan `cetakModal.jenis`; kalau jenis itu
          punya lebih dari satu template, langkah "pilih_template" muncul dulu
          sebelum pratinjau dicetak. */}
      {cetakModal && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={() => setCetakModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="cetak-faktur-modal">
            <div className="sticky top-0 bg-slate-900 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h3 className="font-display font-semibold inline-flex items-center gap-2">
                <Printer className="w-4 h-4" /> Cetak {cetakModal.jenis === "struk" ? "Struk" : "Faktur"}
              </h3>
              <button onClick={() => setCetakModal(null)} className="p-2 hover:bg-white/20 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6">
              {cetakModal.step === "pilih_template" ? (
                <div className="space-y-4">
                  <PemilihTemplate templates={cetakModal.templates} value={cetakModal.templateId}
                    onChange={(id) => setCetakModal(m => ({ ...m, templateId: id }))}
                    jenisLabel={cetakModal.jenis === "struk" ? "struk" : "faktur"} testidPrefix="cetak-tpl" />
                  <div className="flex justify-end">
                    <button onClick={() => muatDokumenCetak(cetakModal.jenis, cetakModal.templates.find(t => t.id === cetakModal.templateId))}
                      data-testid="cetak-tpl-lanjut"
                      className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium">
                      Lanjutkan
                    </button>
                  </div>
                </div>
              ) : cetakModal.step === "kosong" ? (
                <p className="text-sm text-slate-500 text-center py-8" data-testid="cetak-tpl-kosong">
                  Belum ada template {cetakModal.jenis === "struk" ? "struk" : "faktur"}. Buat dulu di menu
                  Template Faktur &amp; Struk.
                </p>
              ) : cetakModal.step === "tanpa_izin" ? (
                <p className="text-sm text-slate-500 text-center py-8" data-testid="cetak-tpl-tanpa-izin">
                  Akun Anda belum punya izin melihat Template Faktur &amp; Struk, jadi format struk tidak bisa
                  dipilih dari sini. Minta admin mencentang modul &quot;Faktur &amp; Struk&quot; pada role Anda.
                </p>
              ) : cetakModal.loading || !cetakModal.data ? (
                <LoadingState loading jumlahKartu={1} data-testid="cetak-faktur-loading" />
              ) : isStruk(cetakModal.data.template) ? (
                <StrukPrint data={cetakModal.data} />
              ) : (
                <FakturPrint data={cetakModal.data} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Dialog konfirmasi PPN sebelum simpan (brief bos) */}
      {ppnConfirm && (
        <div className="fixed inset-0 z-[68] bg-black/60 grid place-items-center p-4" onClick={() => setPpnConfirm(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="ppn-confirm-modal">
            <div className="bg-emerald-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold text-sm">Konfirmasi PPN {PPN_PERSEN_PREVIEW}%</h3>
              <button onClick={() => setPpnConfirm(null)} className="p-1.5 hover:bg-white/20 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-slate-600">
                Transaksi ini akan dicatat KENA PPN. PPN akan dipisah dari pendapatan dan
                dicatat sebagai kewajiban pajak (PPN Keluaran) yang wajib disetor —
                pastikan sudah benar sebelum melanjutkan.
              </p>
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs space-y-0.5">
                <div className="flex justify-between text-slate-600">
                  <span>{diskonNominal > 0 ? "DPP (sesudah diskon, sebelum PPN)" : "DPP (sebelum PPN)"}</span>
                  <span className="font-mono" data-testid="ppn-confirm-dpp">{idr(rincianLayar.dpp)}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>PPN ({PPN_PERSEN_PREVIEW}%)</span>
                  <span className="font-mono">{idr(ppnPreview)}</span>
                </div>
                <div className="flex justify-between font-semibold text-slate-900 pt-0.5 border-t border-slate-200">
                  <span>Total harus dibayar</span>
                  <span className="font-mono">{idr(totalHarusDibayar)}</span>
                </div>
              </div>
            </div>
            <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
              <button onClick={() => setPpnConfirm(null)} data-testid="btn-ppn-kembali"
                className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
                Kembali
              </button>
              <button onClick={() => doSaveSale(ppnConfirm)} disabled={saving} data-testid="btn-ppn-lanjutkan"
                className="h-10 px-5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
                {saving ? "Menyimpan..." : "Lanjutkan"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* PIN PERSETUJUAN HAPUS (brief #23) */}
      {pinDialog && (
        <div className="fixed inset-0 z-[70] bg-black/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={() => setPinDialog(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="pin-dialog">
            <div className="bg-rose-600 text-white px-5 py-3 rounded-t-2xl flex items-center justify-between">
              <h3 className="font-semibold text-sm inline-flex items-center gap-1.5">
                <Lock className="w-4 h-4" /> PIN Persetujuan Hapus
              </h3>
              <button onClick={() => setPinDialog(null)} className="p-1.5 hover:bg-white/20 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-xs text-slate-600">
                Penghapusan butuh persetujuan. Minta PIN ke pemegang izin (lihat Pusat Approval), lalu masukkan di sini.
              </p>
              <input value={pinDialog.pin}
                onChange={e => setPinDialog(d => ({ ...d, pin: e.target.value.replace(/\D/g, "").slice(0, 6), error: "" }))}
                maxLength={6} inputMode="numeric" placeholder="6 digit PIN" autoFocus
                data-testid="pin-input"
                className="w-full h-11 px-3 rounded-lg border border-slate-200 text-center text-lg tracking-[0.3em] font-mono" />
              {pinDialog.error && <p className="text-xs text-rose-600">{pinDialog.error}</p>}
            </div>
            <div className="border-t border-slate-100 p-4 flex justify-end gap-2">
              <button onClick={() => setPinDialog(null)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
                Batal
              </button>
              <button onClick={submitPinDialog} disabled={pinDialog.submitting || pinDialog.pin.length !== 6}
                data-testid="pin-kirim"
                className="h-10 px-5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-medium">
                {pinDialog.submitting ? "Memproses..." : "Kirim & Hapus"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AJUKAN RETUR PENJUALAN (brief bos) — hanya mengirim pengajuan;
          approval + jurnal/stok terjadi di halaman /retur. */}
      {returModal && (
        <div className="fixed inset-0 z-[65] bg-black/60 backdrop-blur-sm grid place-items-center p-4 overflow-y-auto"
          onClick={returGuard.requestClose}>
          <div ref={returGuard.contentRef} className="bg-white rounded-2xl w-full max-w-xl my-8 shadow-2xl" onClick={e => e.stopPropagation()}
            data-testid="retur-ajukan-modal">
            <div className="sticky top-0 bg-rose-600 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
              <h3 className="font-display font-semibold inline-flex items-center gap-2">
                <Undo2 className="w-5 h-5" /> Ajukan Retur Penjualan
              </h3>
              <button onClick={returGuard.requestClose} className="p-2 hover:bg-white/20 rounded-lg" data-testid="retur-ajukan-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              {returModal.loading ? (
                <LoadingState loading jumlahKartu={1} data-testid="retur-ajukan-loading" />
              ) : (
                <>
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">Pilih Barang &amp; Alasan</p>
                    {returModal.items.length === 0 ? (
                      <p className="text-sm text-slate-400 italic">Tidak ada barang di transaksi ini.</p>
                    ) : returModal.items.map((r, i) => (
                      <div key={i}
                        className={`border rounded-lg p-3 ${r.sisa <= 0 ? "border-slate-100 bg-slate-50 opacity-60" : "border-slate-200"}`}>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={r.checked} disabled={r.sisa <= 0}
                            onChange={e => toggleReturItem(i, e.target.checked)}
                            data-testid={`retur-ajukan-cek-${i}`} />
                          <span className="flex-1 text-sm font-medium text-slate-900">{r.nama_barang}</span>
                          {r.isBundleComponent && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 shrink-0">
                              isi paket
                            </span>
                          )}
                          <span className="text-xs font-mono text-slate-500">{idr(r.harga)}</span>
                        </label>
                        {r.komponen && r.komponen.length > 0 && (
                          <div className="mt-2 ml-6 rounded border border-amber-200 bg-amber-50 p-2"
                            data-testid={`retur-ajukan-paket-isi-${i}`}>
                            <p className="text-[11px] font-semibold text-amber-900">
                              Ini baris paket — mencentangnya hanya mengembalikan uangnya.
                            </p>
                            <p className="text-[11px] text-amber-800 mt-0.5">
                              Barangnya tidak ikut kembali ke stok. Supaya barangnya benar-benar
                              kembali, centang juga baris isinya di bawah:
                            </p>
                            <ul className="mt-1 space-y-0.5">
                              {r.komponen.map((k, ki) => (
                                <li key={ki} className="text-[11px] text-amber-900 font-mono">
                                  • {k.nama || k.sku}{k.qty ? ` ×${k.qty}` : ""}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {r.sisa <= 0 ? (
                          <p className="text-[11px] text-slate-400 mt-1 ml-6">Sudah diretur semua ({r.sudah}/{r.qtyAsal})</p>
                        ) : r.checked && (
                          <div className="mt-2 ml-6 grid grid-cols-[110px,1fr] gap-2 items-start">
                            <label className="text-[11px] text-slate-500">
                              Qty (maks {r.sisa})
                              <input type="number" min={1} max={r.sisa} value={r.qtyRetur}
                                onChange={e => setReturQty(i, e.target.value)}
                                data-testid={`retur-ajukan-qty-${i}`}
                                className="mt-0.5 h-8 w-full px-2 rounded border border-slate-200 text-sm" />
                            </label>
                            <label className="text-[11px] text-slate-500">
                              Alasan
                              <input value={r.alasan} onChange={e => setReturAlasan(i, e.target.value)}
                                placeholder="mis. barang cacat / salah pesan"
                                data-testid={`retur-ajukan-alasan-${i}`}
                                className="mt-0.5 h-8 w-full px-2 rounded border border-slate-200 text-sm" />
                            </label>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <F l="Nominal Kembali">
                      <InputRupiah value={returModal.nominal_kembali} testid="retur-ajukan-nominal"
                        onChange={v => setReturModal(m => ({ ...m, nominal_kembali: v || 0, nominalTouched: true }))}
                        className={inp + " text-right font-mono"} />
                      {returModal.diskonNominal > 0 && (
                        <p className="text-[11px] text-slate-500" data-testid="retur-ajukan-nominal-hint">
                          {returModal.diskonPerBaris
                            ? "Otomatis dipotong diskon per barang"
                            : "Otomatis dipotong diskon proporsional"}
                        </p>
                      )}
                      {/* Nota ber-PPN (brief 18 Agu 2026): pelanggan membayar DPP+PPN, jadi
                          batas & saran refund SUDAH mencakup PPN — tanpa hint ini kasir
                          mengira batasnya berhenti di harga barang saja. */}
                      {returModal.kenaPpn && (
                        <p className="text-[11px] text-emerald-700" data-testid="retur-ajukan-ppn-hint">
                          Termasuk PPN {idr(returModal.ppnNominalSale)} — bisa dikembalikan
                          sampai {idr(returModal.sisaNilaiMaks)} (DPP + PPN)
                        </p>
                      )}
                    </F>
                    <F l="Bentuk Pengembalian">
                      {/* Permintaan owner 26 Agu 2026: cash / transfer / TUKAR UNIT.
                          Cash & transfer sama-sama "uang keluar" — bedanya cuma akun
                          mana, jadi keduanya satu pilihan di sini dan akunnya dipilih
                          di bawah. Tukar unit sama sekali bukan uang keluar. */}
                      <select value={returModal.jenisKembali || "uang"}
                        onChange={e => setReturModal(m => ({ ...m, jenisKembali: e.target.value }))}
                        data-testid="retur-ajukan-jenis" className={inp + " bg-white"}>
                        <option value="uang" disabled={!!tagihanReturModal?.ditahan}>
                          Uang kembali (cash / transfer){tagihanReturModal?.ditahan ? " (tidak tersedia)" : ""}
                        </option>
                        <option value="tukar_unit" disabled={!syaratTukarModal?.boleh}>
                          Tukar unit — tahan jadi kredit pelanggan{syaratTukarModal?.boleh ? "" : " (tidak tersedia)"}
                        </option>
                      </select>
                      {returModal.jenisKembali === "tukar_unit" && syaratTukarModal?.boleh && (
                        <p className="text-[11px] text-violet-700 mt-1" data-testid="retur-ajukan-tukar-hint">
                          Tidak ada uang keluar hari ini. Nilainya jadi <b>kredit milik
                          pelanggan</b> dan bisa dipakai sebagai pembayaran di nota
                          berikutnya (metode pembayaran &quot;Kredit Retur&quot;) — HANYA oleh
                          pelanggan atas nama <b>{syaratTukarModal.nama}</b> · No HP <b>{syaratTukarModal.noHp}</b>.
                        </p>
                      )}
                      {/* Alasan ditulis, bukan tooltip — di perangkat sentuh tidak ada hover. */}
                      {tagihanReturModal?.ditahan ? (
                        <p className="text-[11px] text-amber-900 bg-amber-50 border border-amber-200 rounded p-2 mt-1"
                           data-testid="retur-ajukan-tagihan-ditahan">
                          {tagihanReturModal.alasan} Pengembalian uang maupun tukar unit sama-sama
                          belum bisa diajukan untuk nota ini.
                        </p>
                      ) : !syaratTukarModal?.boleh && (
                        <p className="text-[11px] text-amber-800 mt-1" data-testid="retur-ajukan-tukar-terkunci">
                          Tukar unit tidak tersedia: {syaratTukarModal?.alasan}
                        </p>
                      )}
                      {/* Form / petunjuk "minta SPV" / galat: jabatan SPV ke atas
                          (owner 29b) diputuskan di satu komponen untuk dua layar. */}
                      {syaratTukarModal?.perluHp && (
                        <GerbangLengkapiNoHp saleId={returModal.saleId} prefix="retur-ajukan"
                          onSelesai={hp => {
                            setReturModal(m => (m ? { ...m, saleNoHp: hp } : m));
                            setDetail(dt => (dt && dt.id === returModal.saleId ? { ...dt, no_hp: hp } : dt));
                          }} />
                      )}
                    </F>
                    {(returModal.jenisKembali || "uang") !== "tukar_unit" && (
                      <F l="Akun Pengembalian">
                        <select value={returModal.akun} onChange={e => setReturModal(m => ({ ...m, akun: e.target.value }))}
                          data-testid="retur-ajukan-akun" className={inp + " bg-white"}>
                          <option value="">— pilih akun kas/bank —</option>
                          {kasBankAccounts.map(a => (
                            <option key={a.id} value={a.id}>{a.name} ({idr(a.balance)})</option>
                          ))}
                        </select>
                      </F>
                    )}
                  </div>
                  <F l="Catatan (opsional)">
                    <input value={returModal.catatan} onChange={e => setReturModal(m => ({ ...m, catatan: e.target.value }))}
                      data-testid="retur-ajukan-catatan" className={inp} />
                  </F>
                </>
              )}
            </div>
            <div className="border-t border-slate-100 px-6 py-4 flex justify-end gap-2">
              <button onClick={returGuard.requestClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">
                Batal
              </button>
              <button onClick={submitRetur} disabled={returModal.loading || returModal.submitting}
                data-testid="retur-ajukan-submit"
                className="h-10 px-5 rounded-lg bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white text-sm font-medium inline-flex items-center gap-2">
                <Undo2 className="w-4 h-4" /> {returModal.submitting ? "Mengirim..." : "Ajukan Retur"}
              </button>
            </div>
            {returGuard.confirming && (
              <UnsavedGuardBar onStay={returGuard.cancelClose} onDiscard={returGuard.confirmDiscard} testid="retur-ajukan-guard-bar" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Autocomplete nama barang -> master produk (opsional, gerbang izin
// inventory_produk). Barang di luar katalog (jasa, titipan, barang lama)
// TETAP bisa diketik bebas — SKU tidak pernah dipaksa. Kalau lookup gagal
// atau 403, otomatis diam-diam nonaktif tanpa toast merah yang menghalangi.
function ItemNamaField({ index, item, bolehLookup, onPatch, resepBundlingMap }) {
  const [saran, setSaran] = useState([]);
  const debounceRef = useRef(null);
  const reqSeq = useRef(0);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const cari = (teks) => {
    // Kategori di sini murni SARAN cadangan (baris masih ketik bebas, belum
    // tertaut SKU) — begitu `item.sku` sudah terisi (lihat `pilih()` di bawah),
    // baris kategori berubah jadi badge terkunci di JSX dan tebakan ini tidak
    // lagi relevan sampai kasir melepas tautan SKU-nya sendiri.
    onPatch({ nama_barang: teks, kode: deriveKode(teks), kategori: deriveKategoriTeksBebas(teks) });
    if (!bolehLookup) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = teks.trim();
    if (q.length < 2) { setSaran([]); return; }
    debounceRef.current = setTimeout(async () => {
      const seq = ++reqSeq.current;
      try {
        const { data } = await api.get("/inventory/products/lookup", { params: { q } });
        if (seq === reqSeq.current) setSaran(data.items || []);
      } catch {
        // Diam-diam nonaktif — modul lookup mungkin tidak diizinkan atau error jaringan.
        if (seq === reqSeq.current) setSaran([]);
      }
    }, 300);
  };

  const pilih = (p) => {
    // Paket/bundling (poin 8, brief bos 23 Agu 2026) — kalau produk yang
    // dipilih adalah sku_bundle resep aktif, bangun scaffold komponen kosong
    // SEKALIGUS di sini. WAJIB direset di SETIAP pemilihan (bukan hanya kalau
    // def ada) — kalau tidak, baris yang tadinya paket lalu diganti ke barang
    // biasa akan membawa `komponen` basi dari produk sebelumnya.
    const def = resepBundlingMap?.[(p.sku || "").trim().toUpperCase()];
    onPatch({
      nama_barang: p.nama,
      sku: p.sku,
      harga: p.harga_jual || 0,
      kode: deriveKode(p.nama),
      // Kategori diambil APA ADANYA dari master produk — TIDAK ditebak dari
      // nama. `kategori` wajib diisi saat produk dibuat (products.py), jadi
      // `p.kategori` semestinya selalu ada; "" hanya jaring pengaman kalau
      // data lama entah bagaimana kosong (dropdown akan kosong, memaksa
      // kasir memilih manual, alih-alih diam-diam menebak salah).
      kategori: p.kategori || "",
      imei: "",
      komponen: def ? bangunKomponenKosong(def) : null,
    });
    setSaran([]);
  };

  return (
    <div className="relative">
      <input placeholder="Nama Barang" value={item.nama_barang}
        onChange={e => cari(e.target.value)}
        data-testid={`item-cari-${index}`}
        className="h-9 px-3 rounded border border-slate-200 text-sm w-full" />
      {item.sku && (
        <span data-testid={`item-sku-badge-${index}`}
          className="mt-1 inline-flex items-center gap-1 text-[10px] font-mono bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5">
          SKU {item.sku}
          {/* Melepas SKU ikut melepas IMEI + rincian komponen paket yang sudah
              dipilih — keduanya cuma sah selama masih tertaut katalog
              (pemilihnya hilang begitu sku dikosongkan), jadi menyisakannya
              jadi nilai basi yang tidak bisa diedit lagi lewat UI. */}
          <button type="button" onClick={() => onPatch({ sku: "", imei: "", komponen: null })}
            title="Lepas tautan katalog — kembali ke ketik bebas"
            className="text-emerald-600 hover:text-emerald-900 leading-none">×</button>
        </span>
      )}
      {saran.length > 0 && (
        <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {/* Stok habis TIDAK bisa dipilih (keputusan bos). Ditegakkan juga di
              backend supaya tidak bisa ditembus; di sini sekadar mencegah
              kasir terlanjur memilih lalu ditolak saat menyimpan. Barang di
              luar katalog tetap bisa diketik bebas — itu jalan keluarnya.

              Paket (poin 8-11): SKU paket TIDAK PERNAH punya stok fisik
              sendiri (`p.stock_qty` selalu 0, lihat docstring bundling.py) —
              memakai `stock_qty` mentah di sini akan membuat SEMUA paket
              tampak permanen "stok habis" dan tidak bisa dipilih sama sekali.
              Untuk SKU yang cocok resep aktif, "stok" yang dipakai untuk
              keputusan pilih/tidak adalah STOK VIRTUAL (MIN komponen). */}
          {saran.map((p, n) => {
            const defPaket = resepBundlingMap?.[(p.sku || "").trim().toUpperCase()];
            const stokEfektif = defPaket ? (defPaket.stok_virtual?.qty ?? 0) : (p.stock_qty || 0);
            const habis = !(stokEfektif > 0);
            return (
              <button key={p.sku} type="button" disabled={habis}
                onClick={() => { if (!habis) pilih(p); }}
                title={habis ? "Stok habis — terima barangnya dulu lewat Pembelian & Terima" : ""}
                data-testid={`item-saran-${index}-${n}`}
                className={`w-full text-left px-3 py-2 text-xs border-b border-slate-50 last:border-0 ${
                  habis ? "bg-slate-50 opacity-60 cursor-not-allowed" : "hover:bg-emerald-50"}`}>
                <span className="font-medium text-slate-900">{p.nama}</span>
                <span className="font-mono text-[10px] text-slate-400 ml-1.5">{p.sku}</span>
                <span className="text-[10px] text-slate-500 ml-1.5">{idr(p.harga_jual || 0)}</span>
                {defPaket && (
                  <span className="text-[10px] ml-1.5 font-medium text-emerald-700">PAKET</span>
                )}
                <span className={`text-[10px] ml-1.5 font-medium ${habis ? "text-rose-600" : "text-emerald-600"}`}>
                  {habis ? "stok habis — tidak bisa dijual" : `stok ${stokEfektif}`}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ==================== Pemilih IMEI per baris -> unit inventory ====================
// Brief bos poin 4: IMEI HANYA boleh DIPILIH dari unit yang terdaftar &
// berstatus tersedia di inventory — tidak lagi diketik bebas. Beda dari
// ItemNamaField: mengetik di sini TIDAK PERNAH langsung mengisi `item.imei`
// (itu yang membuatnya "ketik bebas"), field baru terisi begitu kasir
// mengklik salah satu SARAN hasil pencarian. Sekali terisi, tampil sebagai
// badge (mirip badge SKU) — untuk mengubahnya, lepas dulu lewat "×".
//
// CATATAN BACKEND (baca sebelum mengandalkan ini sebagai satu-satunya
// pagar): validasi `_tolak_stok_kosong` (routers/sales.py, TIDAK disentuh
// dari sisi frontend ini) menolak IMEI yang tidak terdaftar/tidak
// "tersedia" per BARIS, tapi TIDAK mengunci/mereservasi unit — dua nota
// terpisah yang kebetulan memilih IMEI yang sama di baris yang berbeda
// keduanya akan lolos validasi server (unit tidak pernah ditandai "terjual"
// oleh sales.py, lihat routers/retur.py "BATASAN YANG SENGAJA DIPUTUSKAN").
// Yang bisa dicegah dari sini HANYA duplikat DALAM SATU transaksi yang
// sedang diisi (lewat `imeiTerpakaiLain`) — bukan lintas nota/transaksi.
function ItemImeiField({ index, item, bolehLookup, imeiTerpakaiLain, onPatch, modeEdit }) {
  const [cari, setCari] = useState("");
  const [saran, setSaran] = useState([]);
  const [mencari, setMencari] = useState(false);
  // Harga jual per IMEI (G2) — lihat `lib/hargaUnit.js`; satu penerima respons.
  // Halaman ini HANYA punya jalur cari (tanpa rekomendasi), jadi info harga
  // wajib menggabung respons per SKU — bukan menggantinya tiap ketikan.
  const [infoHarga, setInfoHarga] = useState(null);
  const debounceRef = useRef(null);
  const reqSeq = useRef(0);

  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const cariUnit = (teks) => {
    setCari(teks);
    if (!bolehLookup) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = teks.trim();
    if (q.length < 3) { setSaran([]); setMencari(false); return; }
    debounceRef.current = setTimeout(async () => {
      const seq = ++reqSeq.current;
      setMencari(true);
      try {
        const { data } = await api.get("/inventory/units/pilihan-jual", { params: { q, sku: item.sku } });
        if (seq === reqSeq.current) terimaPilihanJual(data, setSaran, setInfoHarga, item.sku);
      } catch {
        // Diam-diam nonaktif — modul lookup mungkin tidak diizinkan atau error jaringan.
        if (seq === reqSeq.current) setSaran([]);
      } finally {
        if (seq === reqSeq.current) setMencari(false);
      }
    }, 300);
  };

  const pilih = (u) => {
    // Harga per IMEI (G2): hanya menggantikan harga yang masih "otomatis", dan
    // TIDAK saat mengedit penjualan yang sudah tercatat (`modeEdit`).
    const h = hargaSaatPilihUnit({ item, unit: u, info: infoHarga, modeEdit });
    onPatch({ imei: u.identifier, ...h.patch });
    if (h.pesan) toast.info(h.pesan);
    setCari("");
    setSaran([]);
  };

  if (item.imei) {
    return (
      <div className="mt-1">
        <span data-testid={`item-imei-badge-${index}`}
          className="inline-flex items-center gap-1 text-[10px] font-mono bg-violet-50 text-violet-700 border border-violet-200 rounded px-1.5 py-0.5">
          IMEI {item.imei}
          <button type="button" onClick={() => onPatch({ imei: "" })}
            title="Lepas pilihan IMEI"
            className="text-violet-600 hover:text-violet-900 leading-none">×</button>
        </span>
      </div>
    );
  }

  return (
    <div className="relative mt-1">
      <input value={cari} onChange={e => cariUnit(e.target.value)}
        placeholder="Cari IMEI/SN dari stok (opsional)…" data-testid={`item-imei-cari-${index}`}
        className="h-8 px-2 rounded border border-slate-200 text-[11px] w-full font-mono" />
      {saran.length > 0 && (
        <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-40 overflow-y-auto">
          {saran.map((u, n) => {
            const dipakai = imeiTerpakaiLain?.has(u.identifier);
            const info = ringkasInfoUnit(u);
            return (
              <button key={u.id} type="button" disabled={dipakai}
                onClick={() => { if (!dipakai) pilih(u); }}
                title={dipakai ? "Unit ini sudah dipilih di baris lain pada nota ini" : ""}
                data-testid={`item-imei-saran-${index}-${n}`}
                className={`w-full text-left px-2 py-1.5 text-[11px] border-b border-slate-50 last:border-0 ${
                  dipakai ? "bg-slate-50 opacity-60 cursor-not-allowed" : "hover:bg-violet-50"}`}>
                <span className="font-mono font-medium text-slate-900">{u.identifier}</span>
                {/* INV-SN: SN hanya tampilan — yang ditulis ke nota tetap IMEI (identifier). */}
                {u.serial_number && <span className="font-mono text-slate-500 ml-1.5">SN {u.serial_number}</span>}
                {u.warehouse_nama && <span className="text-slate-400 ml-1.5">· {u.warehouse_nama}</span>}
                {/* G2: BH/garansi/harga per IMEI — membedakan HP second sejenis. */}
                {info && <span data-testid={`item-imei-info-${index}-${n}`} className="block text-[10px] text-slate-500">{info}</span>}
                {dipakai && <span className="text-rose-600 ml-1.5">sudah dipilih di baris lain</span>}
              </button>
            );
          })}
        </div>
      )}
      {cari.trim().length >= 3 && !mencari && saran.length === 0 && (
        <p className="text-[10px] text-slate-400 mt-0.5">Tidak ada unit tersedia yang cocok.</p>
      )}
    </div>
  );
}

const inp = "h-10 px-3 rounded-lg border border-slate-200 text-sm w-full focus:outline-none focus:ring-2 focus:ring-emerald-600/40";
function F({ l, children }) {
  return <label className="flex flex-col gap-1"><span className="text-xs font-medium text-slate-600">{l}</span>{children}</label>;
}
function Field({ label, children }) {
  return (
    <div>
      <p className="text-xs text-slate-500 uppercase tracking-wider mb-0.5">{label}</p>
      <div className="text-sm text-slate-900">{children}</div>
    </div>
  );
}
